import React, { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';

export default function TowerDesigner() {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const towerRef = useRef(null);
  const [towerType, setTowerType] = useState('lattice');
  const [sections, setSections] = useState([
    { id: 1, width: 5, height: 5, depth: 6, color: '#94a3b8' },
    { id: 2, width: 4, height: 5, depth: 5, color: '#94a3b8' },
    { id: 3, width: 3, height: 5, depth: 4, color: '#94a3b8' },
	{ id: 4, width: 2, height: 5, depth: 3, color: '#94a3b8' },
    { id: 5, width: 1, height: 5, depth: 2, color: '#94a3b8' },
    { id: 6, width: 1, height: 5, depth: 1, color: '#94a3b8' },
	{ id: 7, width: 1, height: 5, depth: 1, color: '#94a3b8' }
  ]);
  const [microwaves, setMicrowaves] = useState([]);
  const [omnis, setOmnis] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [towerName, setTowerName] = useState('My Tower');
  const [latitude, setLatitude] = useState(42.939645265855816);
  const [longitude, setLongitude] = useState(-88.06162470752568);
  const [towerAzimuth, setTowerAzimuth] = useState(0);
  const [hasShelter, setHasShelter] = useState(false);
  const [shelterDistance, setShelterDistance] = useState(20);
  const [shelterAzimuth, setShelterAzimuth] = useState(0); // Position around tower center
  const [shelterLength, setShelterLength] = useState(3.6576);
  const [shelterWidth, setShelterWidth] = useState(4.8768);
  const [shelterHeight, setShelterHeight] = useState(3.048);
  const [shelterRotation, setShelterRotation] = useState(0); // Rotation of shelter itself
  const [shelterColor, setShelterColor] = useState('#8b7355');
  const shelterRef = useRef(null);
  
  const [hasFence, setHasFence] = useState(false);
  const [fenceLength, setFenceLength] = useState(50);
  const [fenceWidth, setFenceWidth] = useState(50);
  const [fenceHeight, setFenceHeight] = useState(1);
  const [fenceAzimuth, setFenceAzimuth] = useState(0);
  const [fenceColor, setFenceColor] = useState('#0000ff');
  const fenceRef = useRef(null);
  
  const [showSatellite, setShowSatellite] = useState(false);
  const groundRef = useRef(null);
  
  const [coordinateInput, setCoordinateInput] = useState('');
  
  
  const isDragging = useRef(false);
  const previousMouse = useRef({ x: 0, y: 0 });
  
  const [sceneReady, setSceneReady] = useState(false);
  
  const isPanning = useRef(false);
  
  const [units, setUnits] = useState('metric'); // 'metric' or 'imperial'
  
	// Conversion constants
	const M_TO_FT = 3.28084;
	const FT_TO_M = 0.3048;

	// Helper functions for display and conversion
	const toDisplay = (meters) => {
	  return units === 'imperial' ? meters * M_TO_FT : meters;
	};

	const toMeters = (displayValue) => {
	  return units === 'imperial' ? displayValue * FT_TO_M : displayValue;
	};

	const getUnitLabel = () => {
	  return units === 'imperial' ? 'ft' : 'm';
	};
	
	const ConvertedNumberInput = ({ value, onChange, step = "0.1", units, toDisplay, toMeters, className, min, max }) => {
  const [localValue, setLocalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setLocalValue(toDisplay(value).toFixed(2));
    }
  }, [value, isFocused, toDisplay]);

  const handleFocus = (e) => {
    setIsFocused(true);
    setLocalValue(toDisplay(value).toFixed(2));
  };

  const handleBlur = () => {
    setIsFocused(false);
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue)) {
      onChange(toMeters(numValue));
    } else {
      setLocalValue(toDisplay(value).toFixed(2));
    }
  };

  const handleChange = (e) => {
    setLocalValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <input
      type="number"
      step={step}
      min={min}
      max={max}
      value={isFocused ? localValue : toDisplay(value).toFixed(2)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
    />
  );
};
  
  
  //start of satellite ground plane

  
  //end of satellite ground plane
  
  
  

useEffect(() => {
  if (!containerRef.current) return;
  
  let resizeObserver;
  let initialized = false;

  const initializeScene = () => {
    if (!containerRef.current || initialized) return;
    
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    
    if (width === 0 || height === 0) return;
    
    initialized = true;
    
    // Disconnect observer once we have dimensions
    if (resizeObserver) {
      resizeObserver.disconnect();
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x87ceeb);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
    const centerY = totalHeight / 2;
    camera.position.set(30, centerY, 30);
    camera.lookAt(0, centerY, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(10, 20, 10);
    scene.add(directionalLight);

    //const groundGeometry = new THREE.PlaneGeometry(100, 100);
    //const groundMaterial = new THREE.MeshLambertMaterial({ color: 0x22c55e });
    //const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    //ground.rotation.x = -Math.PI / 2;
	//ground.receiveShadow = true;
    //scene.add(ground);

	
	

    const gridHelper = new THREE.GridHelper(100, 20, 0x888888, 0xcccccc);
    scene.add(gridHelper);
	

	

    const animate = () => {
  requestAnimationFrame(animate);
  renderer.render(scene, camera);
};
animate();

setSceneReady(true);




    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
      const centerY = totalHeight / 2;
      cameraRef.current.lookAt(0, centerY, 0);
    };
    window.addEventListener('resize', handleResize);
  };
  
  resizeObserver = new ResizeObserver(() => {
    initializeScene();
  });
  
  resizeObserver.observe(containerRef.current);
  
  return () => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    if (rendererRef.current && containerRef.current && rendererRef.current.domElement) {
      containerRef.current.removeChild(rendererRef.current.domElement);
    }
  };
}, []);

  const createMicrowave = (microwave, baseY, sectionWidth) => {
    const group = new THREE.Group();
    const microwaveColor = parseInt((microwave.color || '#2563eb').replace('#', '0x'));
    
    const mountDistance = sectionWidth * 0.5 + microwave.offset;
    const azimuthRad = (-microwave.azimuth || 0) * Math.PI / 180; //CHANGED THIS to -microwave
    
    const mountX = mountDistance * Math.sin(azimuthRad);
    const mountZ = mountDistance * Math.cos(azimuthRad);
    const edgeX = (sectionWidth * 0.5) * Math.sin(azimuthRad);
    const edgeZ = (sectionWidth * 0.5) * Math.cos(azimuthRad);
    
    const bracketGeometry = new THREE.CylinderGeometry(0.1, 0.1, microwave.offset, 8);
    const bracketMaterial = new THREE.MeshLambertMaterial({ color: 0x1e293b });
    const bracket = new THREE.Mesh(bracketGeometry, bracketMaterial);
    bracket.position.set((edgeX + mountX) / 2, baseY, (edgeZ + mountZ) / 2);
    
    const direction = new THREE.Vector3(mountX - edgeX, 0, mountZ - edgeZ).normalize();
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
    bracket.setRotationFromQuaternion(quaternion);
    group.add(bracket);
    
    let body;
    if (microwave.shape === 'circle') {
      const bodyGeometry = new THREE.CylinderGeometry(microwave.width / 2, microwave.width / 2, microwave.height, 16);
      const bodyMaterial = new THREE.MeshLambertMaterial({ color: microwaveColor });
      body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      const direction = new THREE.Vector3(mountX - edgeX, 0, mountZ - edgeZ).normalize();
      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
      body.setRotationFromQuaternion(quaternion);
      body.position.set(mountX, baseY, mountZ);
    } else {
      const bodyGeometry = new THREE.BoxGeometry(microwave.width, microwave.height, 0.2);
      const bodyMaterial = new THREE.MeshLambertMaterial({ color: microwaveColor });
      body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      body.rotation.y = azimuthRad;
      body.position.set(mountX, baseY + microwave.height / 2, mountZ);
    }
    group.add(body);
    
    const edges = new THREE.EdgesGeometry(body.geometry);
    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
    line.position.copy(body.position);
    line.rotation.copy(body.rotation);
    group.add(line);
    
    return group;
  };

  const createOmni = (omni, baseY, sectionWidth) => {
    const group = new THREE.Group();
    const omniColor = parseInt((omni.color || '#ef4444').replace('#', '0x'));
    
    const mountDistance = sectionWidth * 0.5 + omni.offset;
    const azimuthRad = (-omni.azimuth || 0) * Math.PI / 180; //CHANGED THIS TO -omni
    
    const mountX = mountDistance * Math.sin(azimuthRad);
    const mountZ = mountDistance * Math.cos(azimuthRad);
    const edgeX = (sectionWidth * 0.5) * Math.sin(azimuthRad);
    const edgeZ = (sectionWidth * 0.5) * Math.cos(azimuthRad);
    
    const bracketGeometry = new THREE.CylinderGeometry(0.1, 0.1, omni.offset, 8);
    const bracketMaterial = new THREE.MeshLambertMaterial({ color: 0x1e293b });
    const bracket = new THREE.Mesh(bracketGeometry, bracketMaterial);
    bracket.position.set((edgeX + mountX) / 2, baseY, (edgeZ + mountZ) / 2);
    
    const direction = new THREE.Vector3(mountX - edgeX, 0, mountZ - edgeZ).normalize();
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
    bracket.setRotationFromQuaternion(quaternion);
    group.add(bracket);
    
    const omniGeometry = new THREE.CylinderGeometry(omni.width / 2, omni.width / 2, omni.height, 12);
    const omniMaterial = new THREE.MeshLambertMaterial({ color: omniColor });
    const omniBody = new THREE.Mesh(omniGeometry, omniMaterial);
    omniBody.position.set(mountX, baseY + omni.height / 2, mountZ);
    group.add(omniBody);
    
    const edges = new THREE.EdgesGeometry(omniGeometry);
    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
    line.position.copy(omniBody.position);
    group.add(line);
    
    return group;
  };

  const createMonopole = (section, currentY, isSelected) => {
    const group = new THREE.Group();
    const color = parseInt(section.color.replace('#', '0x'));
    
    const segments = 16;
    const topRadius = section.width / 2;
    const bottomRadius = section.depth / 2;
    
    const geometry = new THREE.CylinderGeometry(topRadius, bottomRadius, section.height, segments);
    const material = new THREE.MeshLambertMaterial({
      color: color,
      emissive: isSelected ? 0x3b82f6 : 0x000000,
      emissiveIntensity: isSelected ? 0.3 : 0,
      metalness: 0.5
    });
    
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = currentY + section.height / 2;
    group.add(mesh);

    const edges = new THREE.EdgesGeometry(geometry);
    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x000000 }));
    line.position.copy(mesh.position);
    group.add(line);

    // Antennas are now rendered separately based on AGL

    return group;
  };

  const createLatticeTower = (section, currentY, isSelected) => {
    const group = new THREE.Group();
    const color = parseInt(section.color.replace('#', '0x'));
    
    const topWidth = section.width;
    const bottomWidth = section.depth;
    const height = section.height;
    
    const tubeRadius = 0.15;
    const material = new THREE.MeshLambertMaterial({
      color: color,
      emissive: isSelected ? 0x3b82f6 : 0x000000,
      emissiveIntensity: isSelected ? 0.3 : 0
    });

    const getCornerPosition = (radius, cornerIndex) => {
      const angle = (cornerIndex * 2 * Math.PI / 3) - Math.PI / 2;
      return { 
        x: radius * Math.cos(angle), 
        z: radius * Math.sin(angle)
      };
    };
    
    for (let i = 0; i < 3; i++) {
      const bottomCorner = getCornerPosition(bottomWidth, i);
      const topCorner = getCornerPosition(topWidth, i);
      
      const bottomX = bottomCorner.x;
      const bottomZ = bottomCorner.z;
      const topX = topCorner.x;
      const topZ = topCorner.z;
      
      const legLength = Math.sqrt(Math.pow(topX - bottomX, 2) + Math.pow(height, 2) + Math.pow(topZ - bottomZ, 2));
      const legGeometry = new THREE.CylinderGeometry(tubeRadius, tubeRadius, legLength, 8);
      const legMesh = new THREE.Mesh(legGeometry, material);
      
      const midX = (bottomX + topX) / 2;
      const midZ = (bottomZ + topZ) / 2;
      legMesh.position.set(midX, currentY + height / 2, midZ);
      
      const direction = new THREE.Vector3(topX - bottomX, height, topZ - bottomZ).normalize();
      const quaternion = new THREE.Quaternion();
      quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
      legMesh.setRotationFromQuaternion(quaternion);
      
      group.add(legMesh);
    }

    const numHorizontals = Math.max(2, Math.floor(height / 3));
    for (let h = 0; h <= numHorizontals; h++) {
      const ratio = h / numHorizontals;
      const levelY = currentY + (ratio * height);
      const levelRadius = bottomWidth + (topWidth - bottomWidth) * ratio;
      
      const corners = [
        getCornerPosition(levelRadius, 0),
        getCornerPosition(levelRadius, 1),
        getCornerPosition(levelRadius, 2)
      ];
      
      for (let i = 0; i < 3; i++) {
        const corner1 = corners[i];
        const corner2 = corners[(i + 1) % 3];
        
        const x1 = corner1.x;
        const z1 = corner1.z;
        const x2 = corner2.x;
        const z2 = corner2.z;
        
        const distance = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(z2 - z1, 2));
        const horizontalGeometry = new THREE.CylinderGeometry(tubeRadius * 0.7, tubeRadius * 0.7, distance, 8);
        const horizontalMesh = new THREE.Mesh(horizontalGeometry, material);
        
        horizontalMesh.position.set((x1 + x2) / 2, levelY, (z1 + z2) / 2);
        
        const direction = new THREE.Vector3(x2 - x1, 0, z2 - z1).normalize();
        
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(direction.x, 0, direction.z));
        horizontalMesh.setRotationFromQuaternion(quaternion);
        
        group.add(horizontalMesh);
      }
    }

    const numPanels = Math.max(1, Math.floor(height / 3));
    for (let p = 0; p < numPanels; p++) {
      const ratio1 = p / numPanels;
      const ratio2 = (p + 1) / numPanels;
      const y1 = currentY + (ratio1 * height);
      const y2 = currentY + (ratio2 * height);
      const width1 = bottomWidth + (topWidth - bottomWidth) * ratio1;
      const width2 = bottomWidth + (topWidth - bottomWidth) * ratio2;
      
      for (let face = 0; face < 3; face++) {
        const corner1_bottom = getCornerPosition(width1, face);
        const corner2_bottom = getCornerPosition(width1, (face + 1) % 3);
        const corner1_top = getCornerPosition(width2, face);
        const corner2_top = getCornerPosition(width2, (face + 1) % 3);
        
        const x1_bl = corner1_bottom.x;
        const z1_bl = corner1_bottom.z;
        const x1_br = corner2_bottom.x;
        const z1_br = corner2_bottom.z;
        const x2_tl = corner1_top.x;
        const z2_tl = corner1_top.z;
        const x2_tr = corner2_top.x;
        const z2_tr = corner2_top.z;
        
        const diag1Length = Math.sqrt(Math.pow(x2_tr - x1_bl, 2) + Math.pow(y2 - y1, 2) + Math.pow(z2_tr - z1_bl, 2));
        const diag1Geometry = new THREE.CylinderGeometry(tubeRadius * 0.6, tubeRadius * 0.6, diag1Length, 6);
        const diag1Mesh = new THREE.Mesh(diag1Geometry, material);
        diag1Mesh.position.set((x1_bl + x2_tr) / 2, (y1 + y2) / 2, (z1_bl + z2_tr) / 2);
        const dir1 = new THREE.Vector3(x2_tr - x1_bl, y2 - y1, z2_tr - z1_bl).normalize();
        const quat1 = new THREE.Quaternion();
        quat1.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir1);
        diag1Mesh.setRotationFromQuaternion(quat1);
        group.add(diag1Mesh);
        
        const diag2Length = Math.sqrt(Math.pow(x2_tl - x1_br, 2) + Math.pow(y2 - y1, 2) + Math.pow(z2_tl - z1_br, 2));
        const diag2Geometry = new THREE.CylinderGeometry(tubeRadius * 0.6, tubeRadius * 0.6, diag2Length, 6);
        const diag2Mesh = new THREE.Mesh(diag2Geometry, material);
        diag2Mesh.position.set((x1_br + x2_tl) / 2, (y1 + y2) / 2, (z1_br + z2_tl) / 2);
        const dir2 = new THREE.Vector3(x2_tl - x1_br, y2 - y1, z2_tl - z1_br).normalize();
        const quat2 = new THREE.Quaternion();
        quat2.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir2);
        diag2Mesh.setRotationFromQuaternion(quat2);
        group.add(diag2Mesh);
      }
    }

    // Antennas are now rendered separately based on AGL

    return group;
  };

  useEffect(() => {
  if (!sceneRef.current || !sceneReady) return;

  if (towerRef.current) {
    sceneRef.current.remove(towerRef.current);
  }

  const tower = new THREE.Group();
  let currentY = 0;

  sections.forEach(section => {
    const isSelected = selectedId === section.id;

    let sectionGroup;
    if (towerType === 'monopole') {
      sectionGroup = createMonopole(section, currentY, isSelected);
    } else {
      sectionGroup = createLatticeTower(section, currentY, isSelected);
    }

    tower.add(sectionGroup);
    currentY += section.height;
  });

  // Helper function to find section and width at a given AGL
  const getSectionDataAtAGL = (agl) => {
    const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
    let heightFromBottom = totalHeight - agl;
    let accumulatedHeight = 0;

    for (let i = sections.length - 1; i >= 0; i--) {
      const section = sections[i];
      if (heightFromBottom <= accumulatedHeight + section.height) {
        // This is the section containing this AGL
        const positionInSection = (heightFromBottom - accumulatedHeight) / section.height;

        // Interpolate width between bottom and top of section
        const bottomWidth = towerType === 'monopole' ? section.depth : section.depth;
        const topWidth = towerType === 'monopole' ? section.width : section.width;
        const width = bottomWidth + (topWidth - bottomWidth) * (1 - positionInSection);

        return {
          section,
          width: towerType === 'monopole' ? width * 2 : width, // Monopole uses diameter
          yPosition: agl
        };
      }
      accumulatedHeight += section.height;
    }

    // Fallback to top section if AGL is above tower
    const topSection = sections[0];
    return {
      section: topSection,
      width: towerType === 'monopole' ? topSection.width * 2 : topSection.width,
      yPosition: agl
    };
  };

  // Render microwaves at their AGL positions
  microwaves.forEach(microwave => {
    const { width, yPosition } = getSectionDataAtAGL(microwave.agl);
    const microwaveGroup = createMicrowave(microwave, yPosition, width);
    tower.add(microwaveGroup);
  });

  // Render omnis at their AGL positions
  omnis.forEach(omni => {
    const { width, yPosition } = getSectionDataAtAGL(omni.agl);
    const omniGroup = createOmni(omni, yPosition, width);
    tower.add(omniGroup);
  });

  sceneRef.current.add(tower);
  towerRef.current = tower;
  
	// Apply tower azimuth rotation with negative to rotate clockwise (East)
	const towerAzimuthRad = ((-towerAzimuth + 180) * Math.PI) / 180;
	tower.rotation.y = towerAzimuthRad;
  
	//Shelter
	
	// Remove old shelter if it exists
	if (shelterRef.current) {
	  sceneRef.current.remove(shelterRef.current);
	  shelterRef.current.geometry.dispose();
	  shelterRef.current.material.dispose();
	  shelterRef.current = null;
	}

	// Add shelter to tower group if enabled (so it rotates with tower)
	if (hasShelter) {
	  const shelterGeometry = new THREE.BoxGeometry(shelterWidth, shelterHeight, shelterLength);
	  const shelterMaterial = new THREE.MeshStandardMaterial({ color: shelterColor });
	  const shelterMesh = new THREE.Mesh(shelterGeometry, shelterMaterial);
	  
		// Position shelter around tower center using shelterAzimuth
		const azimuthRad = (shelterAzimuth * Math.PI) / 180;
		const rotRad = (shelterRotation * Math.PI) / 180;
		shelterMesh.position.set(
		  shelterDistance * Math.sin(azimuthRad),
		  shelterHeight / 2,
		  -shelterDistance * Math.cos(azimuthRad)  // ✅ Added negative sign here
		);
		shelterMesh.rotation.y = rotRad;
	    shelterMesh.castShadow = true;
	  
	  //tower.add(shelterMesh); // Add to tower group instead of scene
	  sceneRef.current.add(shelterMesh); // Add directly to scene, not tower group
	  shelterRef.current = shelterMesh;
	}
	
	//End Shelter
	
	//Start Fence
	
		// Remove old fence if it exists
	if (fenceRef.current) {
	  sceneRef.current.remove(fenceRef.current);
	  fenceRef.current.traverse((child) => {
		if (child.geometry) child.geometry.dispose();
		if (child.material) child.material.dispose();
	  });
	  fenceRef.current = null;
	}

	// Add fence to scene if enabled
	if (hasFence) {
	  const fenceGroup = new THREE.Group();
	  
	  const rotRad = (fenceAzimuth * Math.PI) / 180;
	  
	  // Create 4 walls as thin boxes
	  const hw = fenceLength / 2;
	  const hd = fenceWidth / 2;
	  const wallThickness = 0.1;
	  
	  const wallMaterial = new THREE.MeshStandardMaterial({ color: fenceColor });
	  
	  // Front wall (along width)
	  const frontWall = new THREE.Mesh(
		new THREE.BoxGeometry(fenceLength, fenceHeight, wallThickness),
		wallMaterial
	  );
	  frontWall.position.set(0, fenceHeight / 2, -hd);
	  fenceGroup.add(frontWall);
	  
	  // Back wall
	  const backWall = new THREE.Mesh(
		new THREE.BoxGeometry(fenceLength, fenceHeight, wallThickness),
		wallMaterial
	  );
	  backWall.position.set(0, fenceHeight / 2, hd);
	  fenceGroup.add(backWall);
	  
	  // Left wall (along depth)
	  const leftWall = new THREE.Mesh(
		new THREE.BoxGeometry(wallThickness, fenceHeight, fenceWidth),
		wallMaterial
	  );
	  leftWall.position.set(-hw, fenceHeight / 2, 0);
	  fenceGroup.add(leftWall);
	  
	  // Right wall
	  const rightWall = new THREE.Mesh(
		new THREE.BoxGeometry(wallThickness, fenceHeight, fenceWidth),
		wallMaterial
	  );
	  rightWall.position.set(hw, fenceHeight / 2, 0);
	  fenceGroup.add(rightWall);
	  
	  // Rotate the entire fence group
	  fenceGroup.rotation.y = rotRad;
	  
	  sceneRef.current.add(fenceGroup);
	  fenceRef.current = fenceGroup;
	}
	
	//End Fence
  
  	//NEW COMPASS ROSE
	
	// Create compass rose with lines
	const compassGroup = new THREE.Group();

	// Compass circle
	const circleGeometry = new THREE.RingGeometry(18, 20, 64);
	const circleMaterial = new THREE.MeshBasicMaterial({ 
	  color: 0x333333, 
	  side: THREE.DoubleSide 
	});
	const circle = new THREE.Mesh(circleGeometry, circleMaterial);
	circle.rotation.x = -Math.PI / 2;
	circle.position.y = 0.05;
	compassGroup.add(circle);

	// Create lines for cardinal directions
	const lineLength = 18;

	// North (Red) - negative Z direction
	const northPoints = [new THREE.Vector3(0, 0.1, 0), new THREE.Vector3(0, 0.1, -lineLength)];
	const northGeometry = new THREE.BufferGeometry().setFromPoints(northPoints);
	const northMaterial = new THREE.LineBasicMaterial({ color: 0xff0000, linewidth: 3 });
	const northLine = new THREE.Line(northGeometry, northMaterial);
	compassGroup.add(northLine);

	// Add "N" label
	const nBox = new THREE.Mesh(
	  new THREE.BoxGeometry(2, 0.2, 1),
	  new THREE.MeshBasicMaterial({ color: 0xff0000 })
	);
	nBox.position.set(0, 0.1, -23);
	compassGroup.add(nBox);

	// East (Yellow) - positive X direction
	const eastPoints = [new THREE.Vector3(0, 0.1, 0), new THREE.Vector3(lineLength, 0.1, 0)];
	const eastGeometry = new THREE.BufferGeometry().setFromPoints(eastPoints);
	const eastMaterial = new THREE.LineBasicMaterial({ color: 0xffff00, linewidth: 3 });
	const eastLine = new THREE.Line(eastGeometry, eastMaterial);
	compassGroup.add(eastLine);

	// Add "E" label
	const eBox = new THREE.Mesh(
	  new THREE.BoxGeometry(1, 0.2, 2),
	  new THREE.MeshBasicMaterial({ color: 0xffff00 })
	);
	eBox.position.set(23, 0.1, 0);
	compassGroup.add(eBox);

	// South (White) - positive Z direction
	const southPoints = [new THREE.Vector3(0, 0.1, 0), new THREE.Vector3(0, 0.1, lineLength)];
	const southGeometry = new THREE.BufferGeometry().setFromPoints(southPoints);
	const southMaterial = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 3 });
	const southLine = new THREE.Line(southGeometry, southMaterial);
	compassGroup.add(southLine);

	// Add "S" label
	const sBox = new THREE.Mesh(
	  new THREE.BoxGeometry(2, 0.2, 1),
	  new THREE.MeshBasicMaterial({ color: 0xffffff })
	);
	sBox.position.set(0, 0.1, 23);
	compassGroup.add(sBox);

	// West (Cyan) - negative X direction
	const westPoints = [new THREE.Vector3(0, 0.1, 0), new THREE.Vector3(-lineLength, 0.1, 0)];
	const westGeometry = new THREE.BufferGeometry().setFromPoints(westPoints);
	const westMaterial = new THREE.LineBasicMaterial({ color: 0x00ffff, linewidth: 3 });
	const westLine = new THREE.Line(westGeometry, westMaterial);
	compassGroup.add(westLine);

	// Add "W" label
	const wBox = new THREE.Mesh(
	  new THREE.BoxGeometry(1, 0.2, 2),
	  new THREE.MeshBasicMaterial({ color: 0x00ffff })
	);
	wBox.position.set(-23, 0.1, 0);
	compassGroup.add(wBox);

	sceneRef.current.add(compassGroup)
	
	
	// END COMPASS ROSE
  
  
	//TOWER MARKERS
	
	// Add leg numbers for lattice towers
	// Add leg numbers for lattice and guyed towers
	if ((towerType === 'lattice' || towerType === 'guyed') && sections.length > 0) {
	  const firstSection = [...sections].reverse()[0];
	  const bottomWidth = firstSection.depth;
	  
	  const getCornerPosition = (radius, cornerIndex) => {
		const angle = (cornerIndex * 2 * Math.PI / 3) - Math.PI / 2;
		return { x: radius * Math.cos(angle), z: radius * Math.sin(angle) };
	  };
	  
	  // Add leg markers (RGB for lattice, same for guyed)
	  const legColors = [0xff0000, 0x00ff00, 0x0000ff];
	  
	  for (let i = 0; i < 3; i++) {
		const corner = getCornerPosition(bottomWidth + 5, i);
		const markerGeometry = new THREE.CylinderGeometry(1, 1, 0.1, 32);
		const markerMaterial = new THREE.MeshBasicMaterial({ color: legColors[i] });
		const marker = new THREE.Mesh(markerGeometry, markerMaterial);
		marker.position.set(corner.x, 0.05, corner.z);
		tower.add(marker);
	  }
	  
	  // Add guy anchor markers (only for guyed towers)
	  if (towerType === 'guyed') {
		const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
		const guyDistance = totalHeight * 0.7; // 70% of tower height
		
		for (let i = 0; i < 3; i++) {
		  const corner = getCornerPosition(guyDistance, i);
		  const anchorGeometry = new THREE.CylinderGeometry(1.5, 1.5, 2.0, 32);
		  const anchorMaterial = new THREE.MeshBasicMaterial({ color: 0xff00ff }); // Neon pink
		  const anchor = new THREE.Mesh(anchorGeometry, anchorMaterial);
		  anchor.position.set(corner.x, 0.1, corner.z);
		  tower.add(anchor);
		}
	  }
	}
	//END TOWER MARKERS
  
  
}, [sections, selectedId, towerType, sceneReady, towerAzimuth, hasShelter, shelterDistance, shelterAzimuth, shelterLength, shelterWidth, shelterHeight, shelterRotation, shelterColor, hasFence, fenceLength, fenceWidth, fenceHeight, fenceAzimuth, fenceColor, microwaves, omnis]);




// Start

//start new ground with satellite
	useEffect(() => {
  if (!sceneRef.current || !sceneReady) return;
  
  // Remove old ground if exists
  if (groundRef.current) {
    sceneRef.current.remove(groundRef.current);
    if (groundRef.current.material.map) {
      groundRef.current.material.map.dispose();
    }
    groundRef.current.material.dispose();
    groundRef.current.geometry.dispose();
  }
  
  const groundGeometry = new THREE.PlaneGeometry(100, 100, 1, 1);
  
  if (showSatellite) {
    const zoom = 19;
    const lat = latitude;
    const lon = longitude;
    
    // Convert lat/lon to tile coordinates
    const xtile = (lon + 180) / 360 * Math.pow(2, zoom);
    const ytile = (1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom);
    
    // Center tile
    const centerX = Math.floor(xtile);
    const centerY = Math.floor(ytile);
    
    // Offset within center tile (0 to 1)
    const offsetX = xtile - centerX;
    const offsetY = ytile - centerY;
    
    // Create canvas to composite tiles
    const canvas = document.createElement('canvas');
    canvas.width = 768; // 3 tiles * 256px
    canvas.height = 768;
    const ctx = canvas.getContext('2d');
    
    // Fill with gray as fallback
    ctx.fillStyle = '#cccccc';
    ctx.fillRect(0, 0, 768, 768);
    
    let tilesLoaded = 0;
    const totalTiles = 9;
    let hasError = false;
    
    // Load 3x3 grid of tiles
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const tileX = centerX + dx;
        const tileY = centerY + dy;
        
        const tileSources = [
          `https://mt1.google.com/vt/lyrs=s&x=${tileX}&y=${tileY}&z=${zoom}`,
          `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${tileY}/${tileX}`
        ];
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const tryLoadTile = (sourceIndex) => {
          if (sourceIndex >= tileSources.length) {
            // All sources failed for this tile
            tilesLoaded++;
            if (tilesLoaded === totalTiles) {
              applyTexture();
            }
            return;
          }
          
          img.onload = () => {
            ctx.drawImage(img, (dx + 1) * 256, (dy + 1) * 256, 256, 256);
            tilesLoaded++;
            if (tilesLoaded === totalTiles) {
              applyTexture();
            }
          };
          
          img.onerror = () => {
            tryLoadTile(sourceIndex + 1);
          };
          
          img.src = tileSources[sourceIndex];
        };
        
        tryLoadTile(0);
      }
    }
    
    const applyTexture = () => {
	  if (!sceneRef.current) return;
	  
	  const texture = new THREE.CanvasTexture(canvas);
	  texture.needsUpdate = true;
	  
	  // The center of our 3x3 grid is at pixel (384, 384) in the 768x768 canvas
	  // Our target coordinates are at offsetX, offsetY within the center tile
	  // Center tile spans from (256, 256) to (512, 512) in canvas coordinates
	  
	  // Calculate the exact pixel position of our coordinates in the canvas
	  const pixelX = 256 + (offsetX * 256); // Position within center tile
	  const pixelY = 256 + (offsetY * 256);
	  
	  // We want to show a portion centered on this pixel
	  // Calculate UV coordinates (0 to 1 in canvas space)
	  const centerU = pixelX / 768;
	  const centerV = pixelY / 768;
	  
	  // Show 1/3 of the canvas (256 pixels out of 768)
	  const scale = 1 / 3;
	  
	  // Offset to center the view on our coordinates
	  //texture.offset.set(centerU - scale / 2, centerV - scale / 2);
	  texture.offset.set(centerU - scale / 2, (1 - centerV) - scale / 2);
	  texture.repeat.set(scale, scale);
	  
	  const groundMaterial = new THREE.MeshStandardMaterial({ 
		map: texture, 
		side: THREE.DoubleSide 
	  });
	  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
	  ground.rotation.x = -Math.PI / 2;
	  ground.receiveShadow = true;
	  sceneRef.current.add(ground);
	  groundRef.current = ground;
	};
    
    // Fallback timeout in case tiles don't load
    setTimeout(() => {
      if (tilesLoaded < totalTiles) {
        console.log('Tile loading timeout, applying texture anyway');
        applyTexture();
      }
    }, 5000);
    
  } else {
    // Default green ground
    const groundMaterial = new THREE.MeshStandardMaterial({ color: 0x90EE90, side: THREE.DoubleSide });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    sceneRef.current.add(ground);
    groundRef.current = ground;
  }
}, [showSatellite, latitude, longitude, sceneReady]);
	

// END



  const handleMouseDown = (e) => {
  if (e.button === 1) { // Middle mouse button
    e.preventDefault();
    isPanning.current = true;
    previousMouse.current = { x: e.clientX, y: e.clientY };
  } else if (e.button === 0) { // Left mouse button
    isDragging.current = true;
    previousMouse.current = { x: e.clientX, y: e.clientY };
  }
};

  const handleMouseMove = (e) => {
  if (isPanning.current && cameraRef.current) {
    // Pan the camera
    const deltaX = e.clientX - previousMouse.current.x;
    const deltaY = e.clientY - previousMouse.current.y;
    
    const panSpeed = 0.05;
    cameraRef.current.position.x -= deltaX * panSpeed;
    cameraRef.current.position.y += deltaY * panSpeed;
    
    previousMouse.current = { x: e.clientX, y: e.clientY };
  } else if (isDragging.current && towerRef.current) {
    // Rotate the tower
    const deltaX = e.clientX - previousMouse.current.x;
    towerRef.current.rotation.y += deltaX * 0.01;
    previousMouse.current = { x: e.clientX, y: e.clientY };
  }
};

  const handleMouseUp = (e) => {
  if (e.button === 1) {
    isPanning.current = false;
  } else if (e.button === 0) {
    isDragging.current = false;
  }
};

  const handleWheel = (e) => {
  e.preventDefault();
  if (!cameraRef.current || !rendererRef.current) return;
  
  const canvas = rendererRef.current.domElement;
  const rect = canvas.getBoundingClientRect();
  
  // Get mouse position in normalized device coordinates (-1 to +1)
  const mouse = new THREE.Vector2(
    ((e.clientX - rect.left) / rect.width) * 2 - 1,
    -((e.clientY - rect.top) / rect.height) * 2 + 1
  );
  
  // Create raycaster to find the point in 3D space
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, cameraRef.current);
  
  // Get the direction and calculate a point at a reasonable distance
  const direction = raycaster.ray.direction;
  const distance = cameraRef.current.position.length();
  const target = raycaster.ray.origin.clone().add(direction.multiplyScalar(distance * 0.5));
  
  // Calculate zoom factor
  const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
  
  // Move camera towards/away from the target point
  const toTarget = target.clone().sub(cameraRef.current.position);
  cameraRef.current.position.add(toTarget.multiplyScalar(1 - zoomFactor));
};

  const addSection = () => {
    setSections([...sections, { id: Date.now(), width: 1, height: 5, depth: 1, color: '#94a3b8' }]);
  };

  const deleteSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const duplicateSection = (id) => {
    const section = sections.find(s => s.id === id);
    if (section) {
      const newSection = { ...section, id: Date.now() };
      const index = sections.findIndex(s => s.id === id);
      const newSections = [...sections];
      newSections.splice(index + 1, 0, newSection);
      setSections(newSections);
    }
  };

  const moveSection = (id, direction) => {
    const index = sections.findIndex(s => s.id === id);
    if (direction === 'up' && index > 0) {
      const newSections = [...sections];
      [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
      setSections(newSections);
    } else if (direction === 'down' && index < sections.length - 1) {
      const newSections = [...sections];
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
      setSections(newSections);
    }
  };

  const updateSection = (id, field, value) => {
    setSections(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  // Helper function to calculate total tower height
  const getTotalTowerHeight = () => {
    return sections.reduce((sum, s) => sum + s.height, 0);
  };

  const addMicrowave = () => {
    const totalHeight = getTotalTowerHeight();
    const defaultAGL = totalHeight / 2; // Default to middle of tower
    setMicrowaves([...microwaves, {
      id: Date.now(),
      width: 1,
      height: 2,
      agl: defaultAGL,
      shape: 'square',
      offset: 1,
      azimuth: 60,
      color: '#2563eb'
    }]);
  };

  const deleteMicrowave = (microwaveId) => {
    setMicrowaves(microwaves.filter(m => m.id !== microwaveId));
  };

  const updateMicrowave = (microwaveId, field, value) => {
    setMicrowaves(microwaves.map(m => {
      if (m.id === microwaveId) {
        const updated = { ...m, [field]: value };
        if (field === 'shape' && value === 'circle') {
          updated.offset = 1;
          updated.width = 1;
          updated.height = 0.3;
        }
        return updated;
      }
      return m;
    }));
  };

  const addOmni = () => {
    const totalHeight = getTotalTowerHeight();
    const defaultAGL = totalHeight / 2; // Default to middle of tower
    setOmnis([...omnis, {
      id: Date.now(),
      width: 0.3,
      height: 2,
      agl: defaultAGL,
      offset: 1,
      azimuth: 60,
      color: '#ef4444'
    }]);
  };

  const deleteOmni = (omniId) => {
    setOmnis(omnis.filter(o => o.id !== omniId));
  };

  const updateOmni = (omniId, field, value) => {
    setOmnis(omnis.map(o => o.id === omniId ? { ...o, [field]: value } : o));
  };


const exportToPDF = () => {
    const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
    const totalMicrowaves = microwaves.length;
    const totalOmnis = omnis.length;
    
    let html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${towerName} - Tower Report</title>
  <style>
    @page { margin: 1in; }
    body { 
      font-family: Arial, sans-serif; 
      line-height: 1.6;
      color: #333;
    }
    .header {
      text-align: center;
      border-bottom: 3px solid #2563eb;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    h1 { 
      color: #1e40af; 
      margin: 0;
      font-size: 28px;
    }
    .subtitle {
      color: #64748b;
      font-size: 14px;
      margin-top: 5px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 30px;
      background: #f8fafc;
      padding: 20px;
      border-radius: 8px;
    }
    .info-item {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #e2e8f0;
    }
    .info-label {
      font-weight: bold;
      color: #475569;
    }
    .info-value {
      color: #1e293b;
    }
    .section {
      margin-bottom: 30px;
      page-break-inside: avoid;
    }
    h2 {
      color: #1e40af;
      border-bottom: 2px solid #3b82f6;
      padding-bottom: 8px;
      font-size: 20px;
    }
    h3 {
      color: #0f172a;
      font-size: 16px;
      margin-top: 20px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 15px;
      font-size: 13px;
    }
    th {
      background: #3b82f6;
      color: white;
      padding: 10px;
      text-align: left;
      font-weight: 600;
    }
    td {
      padding: 8px 10px;
      border: 1px solid #e2e8f0;
    }
    tr:nth-child(even) {
      background: #f8fafc;
    }
    .antenna-block {
      background: #eff6ff;
      padding: 15px;
      margin: 10px 0;
      border-left: 4px solid #3b82f6;
      border-radius: 4px;
    }
    .antenna-title {
      font-weight: bold;
      color: #1e40af;
      margin-bottom: 8px;
    }
    .antenna-details {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      font-size: 12px;
    }
    .detail-item {
      padding: 5px;
    }
    .detail-label {
      color: #64748b;
      font-size: 11px;
    }
    .detail-value {
      font-weight: 600;
      color: #0f172a;
    }
    .summary-box {
      background: #dbeafe;
      border: 2px solid #3b82f6;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .summary-title {
      font-size: 18px;
      font-weight: bold;
      color: #1e40af;
      margin-bottom: 15px;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>${towerName}</h1>
    <div class="subtitle">Tower Structure Report</div>
	<p><strong>Tower Azimuth:</strong> ${towerAzimuth}° (KML Export Rotation)</p>
    <div class="subtitle">Generated: ${new Date().toLocaleDateString()}</div>
  </div>

  <div class="summary-box">
    <div class="summary-title">Tower Summary</div>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">Tower Type:</span>
        <span class="info-value">${towerType === 'monopole' ? 'Monopole' : towerType === 'lattice' ? 'Lattice' : 'Guyed'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Total Height:</span>
        <span class="info-value">${toDisplay(totalHeight).toFixed(2)} ${getUnitLabel()}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Latitude:</span>
        <span class="info-value">${latitude.toFixed(6)}°</span>
      </div>
      <div class="info-item">
        <span class="info-label">Longitude:</span>
        <span class="info-value">${longitude.toFixed(6)}°</span>
      </div>
      <div class="info-item">
        <span class="info-label">Number of Sections:</span>
        <span class="info-value">${sections.length}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Total Microwaves:</span>
        <span class="info-value">${totalMicrowaves}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Total Omni Antennas:</span>
        <span class="info-value">${totalOmnis}</span>
      </div>
	  <div class="info-item">
        <span class="info-label">Shelter:</span>
        <span class="info-value">${hasShelter ? 'Yes' : 'No'}</span>
      </div>
    </div>
  </div>

  <div class="section">
    <h2>Section Details</h2>
    <table>
      <thead>
        <tr>
          <th>Section</th>
          <th>Height (${getUnitLabel()})</th>
          <th>${towerType === 'monopole' ? `Top Diam (${getUnitLabel()})` : `Top Width (${getUnitLabel()})`}</th>
          <th>${towerType === 'monopole' ? `Bottom Diam (${getUnitLabel()})` : `Bottom Width (${getUnitLabel()})`}</th>
          <th>AGL Bottom (${getUnitLabel()})</th>
          <th>AGL Top (${getUnitLabel()})</th>
        </tr>
      </thead>
      <tbody>`;
	  
	let overallAGL = 0;
	sections.forEach((section, idx) => {
		overallAGL = overallAGL + section.height;
	});
	
	let tempAGLfromTop = overallAGL;

    sections.reverse().forEach((section, idx) => {
      const topAGL = tempAGLfromTop;
	  const bottomAGL = tempAGLfromTop - section.height;
      
      html += `
        <tr>
          <td>Section ${idx + 1}</td>
          <td>${toDisplay(section.height).toFixed(2)}</td>
          <td>${toDisplay(section.width).toFixed(2)}</td>
          <td>${toDisplay(section.depth).toFixed(2)}</td>
          <td>${toDisplay(bottomAGL).toFixed(2)}</td>
          <td>${toDisplay(topAGL).toFixed(2)}</td>
        </tr>`;
      tempAGLfromTop = bottomAGL;
    });
    
    html += `
      </tbody>
    </table>
  </div>`;

    // Antenna Details

    if (microwaves.length > 0 || omnis.length > 0) {
      html += `
  <div class="section">
    <h2>Antenna Details</h2>`;

      if (microwaves.length > 0) {
        html += `<h3>Microwave Antennas (${microwaves.length})</h3>`;
        microwaves.forEach((mw, mwIdx) => {
          html += `
    <div class="antenna-block">
      <div class="antenna-title">Microwave #${mwIdx + 1}</div>
      <div class="antenna-details">
        <div class="detail-item">
          <div class="detail-label">AGL Height</div>
          <div class="detail-value">${toDisplay(mw.agl).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Shape</div>
          <div class="detail-value">${mw.shape === 'circle' ? 'Circular' : 'Rectangular'}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Width</div>
          <div class="detail-value">${toDisplay(mw.width).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Height</div>
          <div class="detail-value">${toDisplay(mw.height).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Offset</div>
          <div class="detail-value">${toDisplay(mw.offset).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Azimuth</div>
          <div class="detail-value">${mw.azimuth.toFixed(0)}°</div>
        </div>
      </div>
    </div>`;
        });
      }

      if (omnis.length > 0) {
        html += `<h3>Omni-Directional Antennas (${omnis.length})</h3>`;
        omnis.forEach((omni, omniIdx) => {
          const topAGL = omni.agl + omni.height;
          html += `
    <div class="antenna-block">
      <div class="antenna-title">Omni Antenna #${omniIdx + 1}</div>
      <div class="antenna-details">
        <div class="detail-item">
          <div class="detail-label">AGL Bottom</div>
          <div class="detail-value">${toDisplay(omni.agl).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">AGL Top</div>
          <div class="detail-value">${toDisplay(topAGL).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Diameter</div>
          <div class="detail-value">${toDisplay(omni.width).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Height</div>
          <div class="detail-value">${toDisplay(omni.height).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Offset</div>
          <div class="detail-value">${toDisplay(omni.offset).toFixed(2)} ${getUnitLabel()}</div>
        </div>
        <div class="detail-item">
          <div class="detail-label">Azimuth</div>
          <div class="detail-value">${omni.azimuth.toFixed(0)}°</div>
        </div>
      </div>
    </div>`;
        });
      }

      html += `
  </div>`;
    }
	
	
	

    // Engineering Analysis Section
    html += `
  <div class="section" style="page-break-before: always;">
    <h2>Engineering Analysis</h2>`;

    // Calculate engineering parameters
    const towerWeight = sections.reduce((sum, section) => {
      const avgRadius = ((section.width + section.depth) / 4);
      const surfaceArea = 2 * Math.PI * avgRadius * section.height;
      const volume = towerType === 'monopole' 
        ? Math.PI * section.height * (Math.pow(section.width/2, 2) + Math.pow(section.depth/2, 2) + (section.width/2)*(section.depth/2)) / 3
        : surfaceArea * 0.05; // Approximate for lattice
      const weight = volume * (towerType === 'monopole' ? 7850 : 2500); // Steel density kg/m³
      return sum + weight;
    }, 0);

    const windLoadArea = sections.reduce((sum, section) => {
      return sum + (section.width + section.depth) * section.height / 2;
    }, 0);

    const antennaWindLoad = microwaves.reduce((sum, mw) => sum + (mw.width * mw.height), 0) +
                             omnis.reduce((sum, omni) => sum + (omni.width * omni.height), 0);

    const baseWidth = sections[0]?.depth || 0;
    const topWidth = sections[sections.length - 1]?.width || 0;
    
    html += `
    <div class="summary-box">
      <div class="summary-title">Structural Analysis</div>
      <div class="info-grid">
        <div class="info-item">
          <span class="info-label">Estimated Tower Weight:</span>
          <span class="info-value">${(towerWeight / 1000).toFixed(2)} metric tons</span>
        </div>
        <div class="info-item">
          <span class="info-label">Base Width/Diameter:</span>
          <span class="info-value">${toDisplay(baseWidth).toFixed(2)} ${getUnitLabel()}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Top Width/Diameter:</span>
          <span class="info-value">${toDisplay(topWidth).toFixed(2)} ${getUnitLabel()}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Taper Ratio:</span>
          <span class="info-value">${(baseWidth / Math.max(topWidth, 0.1)).toFixed(2)}:1</span>
        </div>
        <div class="info-item">
          <span class="info-label">Height/Base Ratio:</span>
          <span class="info-value">${(totalHeight / baseWidth).toFixed(2)}:1</span>
        </div>
        <div class="info-item">
          <span class="info-label">Tower Wind Load Area:</span>
          <span class="info-value">${toDisplay(windLoadArea).toFixed(2)} ${getUnitLabel()}²</span>
        </div>
        <div class="info-item">
          <span class="info-label">Antenna Wind Load Area:</span>
          <span class="info-value">${toDisplay(antennaWindLoad).toFixed(2)} ${getUnitLabel()}²</span>
        </div>
        <div class="info-item">
          <span class="info-label">Total Exposed Area:</span>
          <span class="info-value">${toDisplay(windLoadArea + antennaWindLoad).toFixed(2)} ${getUnitLabel()}²</span>
        </div>
      </div>
    </div>

    <h3>Wind Load Analysis</h3>
    <table>
      <thead>
        <tr>
          <th>Wind Speed (m/s)</th>
          <th>Wind Pressure (Pa)</th>
          <th>Tower Force (kN)</th>
          <th>Antenna Force (kN)</th>
          <th>Total Force (kN)</th>
          <th>Base Moment (kN·m)</th>
        </tr>
      </thead>
      <tbody>`;

    const windSpeeds = [20, 30, 40, 50, 60];
    windSpeeds.forEach(speed => {
      const pressure = 0.613 * Math.pow(speed, 2); // Wind pressure formula
      const towerForce = (pressure * windLoadArea) / 1000; // kN
      const antennaForce = (pressure * antennaWindLoad) / 1000; // kN
      const totalForce = towerForce + antennaForce;
      const baseMoment = totalForce * (totalHeight / 2); // Approximate moment arm
      
      html += `
        <tr>
          <td>${speed}</td>
          <td>${pressure.toFixed(0)}</td>
          <td>${towerForce.toFixed(2)}</td>
          <td>${antennaForce.toFixed(2)}</td>
          <td>${totalForce.toFixed(2)}</td>
          <td>${baseMoment.toFixed(2)}</td>
        </tr>`;
    });

    html += `
      </tbody>
    </table>

    <h3>Safety Factors & Recommendations</h3>
    <div class="antenna-block">
      <div class="antenna-title">Structural Integrity</div>
      <ul style="margin: 10px 0; padding-left: 20px; line-height: 1.8;">
        <li><strong>Foundation:</strong> Recommended foundation depth: ${toDisplay(totalHeight * 0.1).toFixed(2)} ${getUnitLabel()} minimum</li>
        <li><strong>Guy Wires:</strong> ${totalHeight > 30 ? 'Recommended' : 'Optional'} for heights over ${toDisplay(30).toFixed(0)}${getUnitLabel()}</li>
        <li><strong>Lightning Protection:</strong> Required - Install grounding system with resistance < 10Ω</li>
        <li><strong>Ice Loading:</strong> Consider radial ice thickness of 25-50mm for cold climates</li>
        <li><strong>Seismic Design:</strong> Follow local seismic zone requirements</li>
      </ul>
    </div>

    <div class="antenna-block">
      <div class="antenna-title">Maintenance Schedule</div>
      <ul style="margin: 10px 0; padding-left: 20px; line-height: 1.8;">
        <li><strong>Monthly:</strong> Visual inspection of guy wires, bolts, and antenna mounting</li>
        <li><strong>Quarterly:</strong> Torque check on all critical bolts and connections</li>
        <li><strong>Annual:</strong> Full structural inspection, paint condition assessment</li>
        <li><strong>Biennial:</strong> Professional engineering assessment and load capacity review</li>
        <li><strong>After Severe Weather:</strong> Immediate inspection for damage or displacement</li>
      </ul>
    </div>

    <div class="antenna-block">
      <div class="antenna-title">Regulatory Compliance</div>
      <ul style="margin: 10px 0; padding-left: 20px; line-height: 1.8;">
        <li><strong>FAA Marking:</strong> ${totalHeight > 60 ? 'Required - Red/White paint scheme or lighting' : `Not required for this height (${toDisplay(totalHeight).toFixed(0)}${getUnitLabel()})`}</li>
        <li><strong>Aviation Lighting:</strong> ${totalHeight > 45 ? 'Required - Red obstruction lights' : 'Not required'}</li>
        <li><strong>RF Safety:</strong> Maintain exclusion zones per FCC OET Bulletin 65</li>
        <li><strong>Local Zoning:</strong> Verify compliance with local height restrictions</li>
        <li><strong>Environmental:</strong> Assessment required for wildlife impact</li>
      </ul>
    </div>

    <h3>Antenna Loading Summary</h3>
    <table>
      <thead>
        <tr>
          <th>Type</th>
          <th>Quantity</th>
          <th>Avg AGL (${getUnitLabel()})</th>
          <th>Total Weight (kg)</th>
          <th>Wind Load (${getUnitLabel()}²)</th>
        </tr>
      </thead>
      <tbody>`;

    let microwaveStats = { count: 0, totalWeight: 0, totalArea: 0, totalHeight: 0 };
    let omniStats = { count: 0, totalWeight: 0, totalArea: 0, totalHeight: 0 };

    microwaves.forEach(mw => {
      microwaveStats.count++;
      microwaveStats.totalWeight += mw.width * mw.height * 20; // Approx 20 kg/m²
      microwaveStats.totalArea += mw.width * mw.height;
      microwaveStats.totalHeight += mw.agl;
    });

    omnis.forEach(omni => {
      omniStats.count++;
      omniStats.totalWeight += Math.PI * Math.pow(omni.width/2, 2) * omni.height * 15; // Approx 15 kg/m³
      omniStats.totalArea += omni.width * omni.height;
      omniStats.totalHeight += omni.agl;
    });

    html += `
        <tr>
          <td>Microwave Antennas</td>
          <td>${microwaveStats.count}</td>
          <td>${microwaveStats.count > 0 ? toDisplay(microwaveStats.totalHeight / microwaveStats.count).toFixed(2) : 'N/A'}</td>
          <td>${microwaveStats.totalWeight.toFixed(0)}</td>
          <td>${toDisplay(microwaveStats.totalArea).toFixed(2)}</td>
        </tr>
        <tr>
          <td>Omni Antennas</td>
          <td>${omniStats.count}</td>
          <td>${omniStats.count > 0 ? toDisplay(omniStats.totalHeight / omniStats.count).toFixed(2) : 'N/A'}</td>
          <td>${omniStats.totalWeight.toFixed(0)}</td>
          <td>${toDisplay(omniStats.totalArea).toFixed(2)}</td>
        </tr>
        <tr style="font-weight: bold; background: #dbeafe;">
          <td>TOTAL</td>
          <td>${microwaveStats.count + omniStats.count}</td>
          <td>-</td>
          <td>${(microwaveStats.totalWeight + omniStats.totalWeight).toFixed(0)}</td>
          <td>${toDisplay(microwaveStats.totalArea + omniStats.totalArea).toFixed(2)}</td>
        </tr>`;

    html += `
      </tbody>
    </table>

    <div style="margin-top: 30px; padding: 15px; background: #fef3c7; border-left: 4px solid #f59e0b; border-radius: 4px;">
      <strong style="color: #92400e;">⚠️ Engineering Disclaimer:</strong>
      <p style="margin: 10px 0 0 0; color: #78350f; font-size: 12px; line-height: 1.6;">
        This analysis provides preliminary estimates only. A licensed professional engineer must perform detailed structural analysis, 
        foundation design, and wind load calculations according to local building codes (TIA-222, ASCE 7, or equivalent) before construction. 
        All values are approximations and should not be used for final design or permitting purposes.
      </p>
    </div>
  </div>`;

    html += `
</body>
</html>`;

    // Create a new window and print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    
    // Wait for content to load, then trigger print dialog
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };



  const exportToKML = () => {
    alert('Exporting KML with complete 3D model...');
    
    // Will implement the export here
    const fileName = towerName.replace(/\s+/g, '_');
    
    // Download KML
    const kmlContent = generateKML(fileName);
    downloadFile(kmlContent, fileName + '.kml', 'application/vnd.google-earth.kml+xml');
    
    // Download DAE  
    setTimeout(() => {
      const daeContent = generateDAE();
      downloadFile(daeContent, fileName + '_model.dae', 'application/xml');
      alert('Downloaded 2 files! Place both in the same folder and open the KML in Google Earth.');
    }, 500);
  };
  
  

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const generateKML = (fileName) => {
    const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
    
    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${towerName}</name>
    <Placemark>
      <name>${towerName}</name>
      <description>Height: ${totalHeight}m</description>
      <Model>
        <altitudeMode>relativeToGround</altitudeMode>
        <Location>
          <longitude>${longitude}</longitude>
          <latitude>${latitude}</latitude>
          <altitude>0</altitude>
        </Location>
        <Orientation>
          <heading>${towerAzimuth}</heading>
          <tilt>0</tilt>
          <roll>0</roll>
        </Orientation>
        <Scale>
          <x>1</x>
          <y>1</y>
          <z>1</z>
        </Scale>
        <Link>
          <href>${fileName}_model.dae</href>
        </Link>
      </Model>
    </Placemark>
  </Document>
</kml>`;
  };

  const generateDAE = () => {
    // Helper function to create tube geometry vertices
    const createTubeVertices = (x1, y1, z1, x2, y2, z2, radius, segments = 8) => {
      const vertices = [];
      const indices = [];
      
      const dx = x2 - x1;
      const dy = y2 - y1;
      const dz = z2 - z1;
      const length = Math.sqrt(dx*dx + dy*dy + dz*dz);
      
      if (length < 0.01) return { vertices, indices };
      
      const dirX = dx / length;
      const dirY = dy / length;
      const dirZ = dz / length;
      
      // Better perpendicular vector calculation that works for all orientations
      let perpX, perpY, perpZ;
      if (Math.abs(dirX) < 0.9) {
        // Use X-axis as reference
        perpX = 0;
        perpY = dirZ;
        perpZ = -dirY;
      } else if (Math.abs(dirY) < 0.9) {
        // Use Y-axis as reference
        perpX = -dirZ;
        perpY = 0;
        perpZ = dirX;
      } else {
        // Use Z-axis as reference
        perpX = dirY;
        perpY = -dirX;
        perpZ = 0;
      }
      
      const perpLen = Math.sqrt(perpX*perpX + perpY*perpY + perpZ*perpZ);
      if (perpLen > 0.001) {
        perpX /= perpLen;
        perpY /= perpLen;
        perpZ /= perpLen;
      } else {
        // Fallback
        perpX = 1;
        perpY = 0;
        perpZ = 0;
      }
      
      const perp2X = dirY*perpZ - dirZ*perpY;
      const perp2Y = dirZ*perpX - dirX*perpZ;
      const perp2Z = dirX*perpY - dirY*perpX;
      
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const cos = Math.cos(angle) * radius;
        const sin = Math.sin(angle) * radius;
        
        vertices.push([
          x1 + perpX * cos + perp2X * sin,
          y1 + perpY * cos + perp2Y * sin,
          z1 + perpZ * cos + perp2Z * sin
        ]);
        
        vertices.push([
          x2 + perpX * cos + perp2X * sin,
          y2 + perpY * cos + perp2Y * sin,
          z2 + perpZ * cos + perp2Z * sin
        ]);
      }
      
      for (let i = 0; i < segments; i++) {
        const curr = i * 2;
        const next = ((i + 1) % segments) * 2;
        
        indices.push(curr, curr + 1, next);
        indices.push(curr + 1, next + 1, next);
      }
      
      return { vertices, indices };
    };

    const getCornerPosition = (radius, cornerIndex) => {
      const angle = (cornerIndex * 2 * Math.PI / 3) - Math.PI / 2;
      return { x: radius * Math.cos(angle), z: radius * Math.sin(angle) };
    };

    let allGeometries = [];
    let materialId = 0;

    const addGeometry = (verts, inds, color, name) => {
      const matId = 'mat' + materialId;
      materialId++;
      allGeometries.push({ vertices: verts, indices: inds, color: color, matId: matId, name: name });
    };

    let currentY = 0;
    
    sections.forEach((section, sectionIdx) => {
      if (towerType === 'monopole') {
        // Monopole cylinder
        const segments = 16;
        const topRadius = section.width / 2;
        const bottomRadius = section.depth / 2;
        const height = section.height;
        
        const vertices = [];
        const indices = [];
        
        for (let i = 0; i < segments; i++) {
          const angle = (i / segments) * Math.PI * 2;
          vertices.push([
            Math.cos(angle) * bottomRadius,
            currentY,
            Math.sin(angle) * bottomRadius
          ]);
          vertices.push([
            Math.cos(angle) * topRadius,
            currentY + height,
            Math.sin(angle) * topRadius
          ]);
        }
        
        for (let i = 0; i < segments; i++) {
          const current = i * 2;
          const next = ((i + 1) % segments) * 2;
          indices.push(current, current + 1, next);
          indices.push(current + 1, next + 1, next);
        }
        
        addGeometry(vertices, indices, section.color, 'monopole_' + sectionIdx);
        
      } else {
        // Lattice tower
        const topWidth = section.width;
        const bottomWidth = section.depth;
        const height = section.height;
        const tubeRadius = 0.15;
        
        // Main legs
        for (let i = 0; i < 3; i++) {
          const bottomCorner = getCornerPosition(bottomWidth, i);
          const topCorner = getCornerPosition(topWidth, i);
          
          const leg = createTubeVertices(
            bottomCorner.x, currentY, bottomCorner.z,
            topCorner.x, currentY + height, topCorner.z,
            tubeRadius
          );
          addGeometry(leg.vertices, leg.indices, section.color, 'leg_' + sectionIdx + '_' + i);
        }
        
        // Horizontals
        const numHorizontals = Math.max(2, Math.floor(height / 3));
        for (let h = 0; h <= numHorizontals; h++) {
          const ratio = h / numHorizontals;
          const levelY = currentY + (ratio * height);
          const levelRadius = bottomWidth + (topWidth - bottomWidth) * ratio;
          
          for (let i = 0; i < 3; i++) {
            const corner1 = getCornerPosition(levelRadius, i);
            const corner2 = getCornerPosition(levelRadius, (i + 1) % 3);
            
            const horiz = createTubeVertices(
              corner1.x, levelY, corner1.z,
              corner2.x, levelY, corner2.z,
              tubeRadius * 0.7
            );
            addGeometry(horiz.vertices, horiz.indices, section.color, 'horiz_' + sectionIdx + '_' + h + '_' + i);
          }
        }
        
        // Diagonals
        const numPanels = Math.max(1, Math.floor(height / 3));
        for (let p = 0; p < numPanels; p++) {
          const ratio1 = p / numPanels;
          const ratio2 = (p + 1) / numPanels;
          const y1 = currentY + (ratio1 * height);
          const y2 = currentY + (ratio2 * height);
          const width1 = bottomWidth + (topWidth - bottomWidth) * ratio1;
          const width2 = bottomWidth + (topWidth - bottomWidth) * ratio2;
          
          for (let face = 0; face < 3; face++) {
            const corner1_bottom = getCornerPosition(width1, face);
            const corner2_bottom = getCornerPosition(width1, (face + 1) % 3);
            const corner1_top = getCornerPosition(width2, face);
            const corner2_top = getCornerPosition(width2, (face + 1) % 3);
            
            const diag1 = createTubeVertices(
              corner1_bottom.x, y1, corner1_bottom.z,
              corner2_top.x, y2, corner2_top.z,
              tubeRadius * 0.6
            );
            addGeometry(diag1.vertices, diag1.indices, section.color, 'diag1_' + sectionIdx + '_' + p + '_' + face);
            
            const diag2 = createTubeVertices(
              corner2_bottom.x, y1, corner2_bottom.z,
              corner1_top.x, y2, corner1_top.z,
              tubeRadius * 0.6
            );
            addGeometry(diag2.vertices, diag2.indices, section.color, 'diag2_' + sectionIdx + '_' + p + '_' + face);
          }
        }
		
		//start new cylinder generation function
		
				function buildCylinder({
				  cx, cz,
				  radius = 1.5,
				  height = 1,
				  segments = 16,
				  yEpsilon = 0.05,
				  doubleSided = false
				}) {
		  // sanity: need at least a triangle
		  segments = Math.max(3, Math.floor(segments) || 16);

		  const verts = [];
		  const inds  = [];

		  const y0 = yEpsilon;
		  const y1 = y0 + height;

		  // bottom ring
		  for (let j = 0; j < segments; j++) {
			const a = (j / segments) * Math.PI * 2;
			const x = cx + Math.cos(a) * radius;
			const z = cz + Math.sin(a) * radius;
			verts.push([x, y0, z]);           // 0 .. segments-1
		  }
		  // top ring
		  for (let j = 0; j < segments; j++) {
			const a = (j / segments) * Math.PI * 2;
			const x = cx + Math.cos(a) * radius;
			const z = cz + Math.sin(a) * radius;
			verts.push([x, y1, z]);           // segments .. 2*segments-1
		  }

		  // cap centers
		  const bottomCenterIndex = verts.length;
		  verts.push([cx, y0, cz]);
		  const topCenterIndex = verts.length;
		  verts.push([cx, y1, cz]);

		  // sides (CCW from outside)
		  for (let j = 0; j < segments; j++) {
			const next = (j + 1) % segments;
			const bj = j;
			const tj = j + segments;
			const bn = next;
			const tn = next + segments;
			inds.push(bj, tj, bn);
			inds.push(bn, tj, tn);
		  }

		  // bottom cap (normal -Y). CCW when viewed from below
		  for (let j = 0; j < segments; j++) {
			const next = (j + 1) % segments;
			inds.push(bottomCenterIndex, next, j);
		  }

		  // top cap (normal +Y). CCW when viewed from above
		  for (let j = 0; j < segments; j++) {
			const next = (j + 1) % segments;
			inds.push(topCenterIndex, j + segments, next + segments);
		  }

		  // ✅ FIX: capture original length before pushing reversed tris
		  if (doubleSided) {
			const original = inds.length;
			for (let t = 0; t < original; t += 3) {
			  inds.push(inds[t + 2], inds[t + 1], inds[t + 0]);
			}
		  }

		  return { verts, inds };
		}

		
		//end new cylindar generation function
		
		
		
		//START ADD GUYED Anchor
	  
	  // Add guy anchor markers for guyed towers (pink markers at 70% of height)
		// --- USE IN YOUR LOOP ---
		if (towerType === 'guyed') {
		  const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
		  const guyDistance = totalHeight * 0.7;

		  for (let i = 0; i < 3; i++) {
			const corner = getCornerPosition(guyDistance, i);

			const { verts: anchorVerts, inds: anchorInds } = buildCylinder({
			  cx: corner.x,
			  cz: corner.z,
			  radius: 1.5,
			  height: 1,
			  segments: 16,
			  yEpsilon: 0.05,       // tweak if terrain still eats it
			  doubleSided: true     // turn off if you add GE double_sided material
			});

			addGeometry(anchorVerts, anchorInds, '#ff00ff', 'guy_anchor_' + i);
		  }
		}
	  //END ADD GUYED ANCHOR
		
      }
	  
	  
	  
	  
	  
	  //START add shelter
	 // Add shelter to DAE if enabled
	if (hasShelter) {
		const hw = shelterLength / 2;
		const hh = shelterHeight / 2;
		const hd = shelterWidth / 2;
		
		// Counter-rotate shelter and add 180 degree offset for coordinate system
		const adjustedAzimuth = shelterAzimuth - towerAzimuth + 180;
		const azimuthRad = (adjustedAzimuth * Math.PI) / 180;
		const rotRad = ((shelterRotation - towerAzimuth + 180) * Math.PI) / 180;
		const shelterX = shelterDistance * Math.sin(azimuthRad);
		const shelterZ = -shelterDistance * Math.cos(azimuthRad);
		
		const cos = Math.cos(rotRad);
		const sin = Math.sin(rotRad);
		
		// Define 8 corners of the box
		const localCorners = [
		  [-hw, 0, -hd],      // 0: bottom front left
		  [hw, 0, -hd],       // 1: bottom front right
		  [hw, 0, hd],        // 2: bottom back right
		  [-hw, 0, hd],       // 3: bottom back left
		  [-hw, shelterHeight, -hd],  // 4: top front left
		  [hw, shelterHeight, -hd],   // 5: top front right
		  [hw, shelterHeight, hd],    // 6: top back right
		  [-hw, shelterHeight, hd]    // 7: top back left
		];
		
		// Rotate and position the corners
		const rotatedCorners = localCorners.map(([lx, ly, lz]) => {
		  const rx = shelterX + (lx * cos - lz * sin);
		  const ry = ly;
		  const rz = shelterZ + (lx * sin + lz * cos);
		  return [rx, ry, rz];
		});
		
		// Build all 6 faces with BOTH front and back faces (double-sided)
		const allFaces = [];
		
		// Each face gets drawn twice - once for each side
		const faces = [
		  // Bottom face
		  [0, 1, 2], [0, 2, 3],
		  [0, 2, 1], [0, 3, 2],  // Reverse winding
		  // Top face
		  [4, 5, 6], [4, 6, 7],
		  [4, 6, 5], [4, 7, 6],  // Reverse winding
		  // Front face
		  [0, 1, 5], [0, 5, 4],
		  [0, 5, 1], [0, 4, 5],  // Reverse winding
		  // Back face
		  [2, 3, 7], [2, 7, 6],
		  [2, 7, 3], [2, 6, 7],  // Reverse winding
		  // Left face
		  [3, 0, 4], [3, 4, 7],
		  [3, 4, 0], [3, 7, 4],  // Reverse winding
		  // Right face
		  [1, 2, 6], [1, 6, 5],
		  [1, 6, 2], [1, 5, 6]   // Reverse winding
		];
		
		const shelterIndices = faces.flat();
		
		addGeometry(rotatedCorners, shelterIndices, shelterColor, 'shelter');
	}
	//END ADD Shelter
	
	//START ADD Fence
	// Add fence to DAE if enabled
	if (hasFence) {
	  const hw = fenceLength / 2;
	  const hh = fenceHeight / 2;
	  const hd = fenceWidth / 2;
	  const wallThickness = 0.1;
	  
	  const rotRad = ((fenceAzimuth - towerAzimuth + 180) * Math.PI) / 180;
	  const cos = Math.cos(rotRad);
	  const sin = Math.sin(rotRad);
	  
	  // Create 4 walls
	  const walls = [
		// Front wall (along length, at -width/2)
		{ lx: -hw, ly: 0, lz: -hd, w: fenceLength, h: fenceHeight, d: wallThickness, rotY: 0 },
		// Back wall (along length, at +width/2)
		{ lx: -hw, ly: 0, lz: hd - wallThickness, w: fenceLength, h: fenceHeight, d: wallThickness, rotY: 0 },
		// Left wall (along width, at -length/2)
		{ lx: -hw, ly: 0, lz: -hd, w: wallThickness, h: fenceHeight, d: fenceWidth, rotY: 0 },
		// Right wall (along width, at +length/2)
		{ lx: hw - wallThickness, ly: 0, lz: -hd, w: wallThickness, h: fenceHeight, d: fenceWidth, rotY: 0 }
	  ];
	  
	  walls.forEach((wall, wallIdx) => {
		const corners = [
		  [wall.lx, wall.ly, wall.lz],
		  [wall.lx + wall.w, wall.ly, wall.lz],
		  [wall.lx + wall.w, wall.ly, wall.lz + wall.d],
		  [wall.lx, wall.ly, wall.lz + wall.d],
		  [wall.lx, wall.ly + wall.h, wall.lz],
		  [wall.lx + wall.w, wall.ly + wall.h, wall.lz],
		  [wall.lx + wall.w, wall.ly + wall.h, wall.lz + wall.d],
		  [wall.lx, wall.ly + wall.h, wall.lz + wall.d]
		];
		
		const rotatedCorners = corners.map(([lx, ly, lz]) => {
		  const rx = (lx * cos - lz * sin);
		  const ry = ly;
		  const rz = (lx * sin + lz * cos);
		  return [rx, ry, rz];
		});
		
		const faceIndices = [
		  0, 1, 2, 0, 2, 3,  // Bottom
		  4, 6, 5, 4, 7, 6,  // Top
		  0, 5, 1, 0, 4, 5,  // Front
		  1, 6, 2, 1, 5, 6,  // Right
		  2, 7, 3, 2, 6, 7,  // Back
		  3, 4, 0, 3, 7, 4,  // Left
		  // Double-sided
		  0, 2, 1, 0, 3, 2,
		  4, 5, 6, 4, 6, 7,
		  0, 1, 5, 0, 5, 4,
		  1, 2, 6, 1, 6, 5,
		  2, 3, 7, 2, 7, 6,
		  3, 0, 4, 3, 4, 7
		];
		
		addGeometry(rotatedCorners, faceIndices, fenceColor, 'fence_wall_' + wallIdx);
	  });
	}
	
	//END ADD FENCE
      
      currentY += section.height;
    });

    // Helper function to calculate section width at a given AGL height
    const getSectionWidthAtAGL = (agl) => {
      let accumulatedHeight = 0;
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        if (agl >= accumulatedHeight && agl <= accumulatedHeight + section.height) {
          // Found the section - calculate width at this height
          const ratio = (agl - accumulatedHeight) / section.height;
          if (towerType === 'monopole') {
            const bottomRadius = section.depth / 2;
            const topRadius = section.width / 2;
            const radiusAtAGL = bottomRadius + (topRadius - bottomRadius) * ratio;
            return radiusAtAGL * 2;
          } else {
            // Lattice tower
            return section.depth + (section.width - section.depth) * ratio;
          }
        }
        accumulatedHeight += section.height;
      }
      // If not found, return last section's top width
      const lastSection = sections[sections.length - 1];
      return towerType === 'monopole' ? lastSection.width : lastSection.width;
    };

    // Render global microwaves array
    microwaves.forEach((mw, mwIdx) => {
      const mwY = mw.agl;
      const azimuthRad = (-mw.azimuth || 0) * Math.PI / 180;
      const sectionWidth = getSectionWidthAtAGL(mw.agl);
      const mountDist = (towerType === 'monopole' ? sectionWidth * 0.5 : sectionWidth) + mw.offset;
      const mountX = mountDist * Math.sin(azimuthRad);
      const mountZ = mountDist * Math.cos(azimuthRad);
      const edgeX = (towerType === 'monopole' ? sectionWidth * 0.5 : sectionWidth) * Math.sin(azimuthRad);
      const edgeZ = (towerType === 'monopole' ? sectionWidth * 0.5 : sectionWidth) * Math.cos(azimuthRad);

      // Bracket
      const bracket = createTubeVertices(edgeX, mwY, edgeZ, mountX, mwY, mountZ, 0.35, 12);
      if (bracket.vertices.length > 0) {
        addGeometry(bracket.vertices, bracket.indices, '#000000', 'mw_bracket_' + mwIdx);
      }

      // Antenna body
      if (mw.shape === 'circle') {
        const body = createTubeVertices(mountX, mwY, mountZ, mountX + Math.sin(azimuthRad) * mw.height, mwY, mountZ + Math.cos(azimuthRad) * mw.height, mw.width / 2);
        if (body.vertices.length > 0) {
          addGeometry(body.vertices, body.indices, mw.color || '#2563eb', 'mw_' + mwIdx);
        }
      } else {
        // Box antenna
        const hw = mw.width / 2;
        const cos = Math.cos(azimuthRad);
        const sin = Math.sin(azimuthRad);

        const boxVerts = [
          [mountX - hw * cos, mwY, mountZ - hw * sin],
          [mountX + hw * cos, mwY, mountZ + hw * sin],
          [mountX - hw * cos, mwY + mw.height, mountZ - hw * sin],
          [mountX + hw * cos, mwY + mw.height, mountZ + hw * sin],
          [mountX - hw * cos - 0.1 * sin, mwY, mountZ - hw * sin + 0.1 * cos],
          [mountX + hw * cos - 0.1 * sin, mwY, mountZ + hw * sin + 0.1 * cos],
          [mountX - hw * cos - 0.1 * sin, mwY + mw.height, mountZ - hw * sin + 0.1 * cos],
          [mountX + hw * cos - 0.1 * sin, mwY + mw.height, mountZ + hw * sin + 0.1 * cos]
        ];

        const boxIndices = [0, 2, 1, 1, 2, 3, 4, 5, 6, 5, 7, 6, 0, 1, 4, 1, 5, 4, 2, 6, 3, 3, 6, 7, 0, 4, 2, 2, 4, 6, 1, 3, 5, 3, 7, 5];
        addGeometry(boxVerts, boxIndices, mw.color || '#2563eb', 'mw_' + mwIdx);
      }
    });

    // Render global omnis array
    omnis.forEach((omni, omniIdx) => {
      const omniY = omni.agl;
      const azimuthRad = (-omni.azimuth || 0) * Math.PI / 180;
      const sectionWidth = getSectionWidthAtAGL(omni.agl);
      const mountDist = (towerType === 'monopole' ? sectionWidth * 0.5 : sectionWidth) + omni.offset;
      const mountX = mountDist * Math.sin(azimuthRad);
      const mountZ = mountDist * Math.cos(azimuthRad);
      const edgeX = (towerType === 'monopole' ? sectionWidth * 0.5 : sectionWidth) * Math.sin(azimuthRad);
      const edgeZ = (towerType === 'monopole' ? sectionWidth * 0.5 : sectionWidth) * Math.cos(azimuthRad);

      console.log('Omni Bracket #' + omniIdx + ':', 'from', edgeX.toFixed(2), omniY.toFixed(2), edgeZ.toFixed(2), 'to', mountX.toFixed(2), omniY.toFixed(2), mountZ.toFixed(2), 'azimuth:', omni.azimuth);

      // Bracket
      const bracket = createTubeVertices(edgeX, omniY, edgeZ, mountX, omniY, mountZ, 0.35, 12);
      if (bracket.vertices.length > 0) {
        addGeometry(bracket.vertices, bracket.indices, '#000000', 'omni_bracket_' + omniIdx);
        console.log('  -> Created omni bracket with', bracket.vertices.length, 'vertices');

        // Add a small sphere at the connection point for visibility
        const connectorVerts = [];
        const connectorInds = [];
        const r = 0.25;
        for (let lat = 0; lat <= 8; lat++) {
          const theta = lat * Math.PI / 8;
          const sinTheta = Math.sin(theta);
          const cosTheta = Math.cos(theta);
          for (let lon = 0; lon <= 8; lon++) {
            const phi = lon * 2 * Math.PI / 8;
            const x = edgeX + r * sinTheta * Math.cos(phi);
            const y = omniY + r * cosTheta;
            const z = edgeZ + r * sinTheta * Math.sin(phi);
            connectorVerts.push([x, y, z]);
          }
        }
        for (let lat = 0; lat < 8; lat++) {
          for (let lon = 0; lon < 8; lon++) {
            const first = lat * 9 + lon;
            const second = first + 9;
            connectorInds.push(first, second, first + 1);
            connectorInds.push(second, second + 1, first + 1);
          }
        }
        addGeometry(connectorVerts, connectorInds, '#ffff00', 'omni_connector_' + omniIdx);
      } else {
        console.log('  -> WARNING: Bracket has no vertices!');
      }

      // Vertical cylinder
      const body = createTubeVertices(mountX, omniY, mountZ, mountX, omniY + omni.height, mountZ, omni.width / 2);
      if (body.vertices.length > 0) {
        addGeometry(body.vertices, body.indices, omni.color || '#ef4444', 'omni_' + omniIdx);
      }
    });

    // Now build the DAE XML
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: (parseInt(result[1], 16) / 255).toFixed(3),
        g: (parseInt(result[2], 16) / 255).toFixed(3),
        b: (parseInt(result[3], 16) / 255).toFixed(3)
      } : { r: 0.5, g: 0.5, b: 0.5 };
    };

    let dae = '<?xml version="1.0" encoding="UTF-8"?>\n';
    dae += '<COLLADA xmlns="http://www.collada.org/2005/11/COLLADASchema" version="1.4.1">\n';
    dae += '  <asset>\n';
    dae += '    <up_axis>Y_UP</up_axis>\n';
    dae += '  </asset>\n';
    dae += '  <library_materials>\n';
    
    allGeometries.forEach(geom => {
      dae += '    <material id="' + geom.matId + '">\n';
      dae += '      <instance_effect url="#' + geom.matId + '_effect"/>\n';
      dae += '    </material>\n';
    });
    
    dae += '  </library_materials>\n';
    dae += '  <library_effects>\n';
    
    allGeometries.forEach(geom => {
      const rgb = hexToRgb(geom.color);
      dae += '    <effect id="' + geom.matId + '_effect">\n';
      dae += '      <profile_COMMON>\n';
      dae += '        <technique sid="common">\n';
      dae += '          <phong>\n';
      dae += '            <diffuse>\n';
      dae += '              <color>' + rgb.r + ' ' + rgb.g + ' ' + rgb.b + ' 1</color>\n';
      dae += '            </diffuse>\n';
      dae += '          </phong>\n';
      dae += '        </technique>\n';
	  dae += '        <extra>\n';
      dae += '          <technique profile="GOOGLEEARTH">\n';
      dae += '            <double_sided>1</double_sided>\n';
      dae += '          </technique>\n';
      dae += '        </extra>\n';
      dae += '      </profile_COMMON>\n';
      dae += '    </effect>\n';
    });
    
    dae += '  </library_effects>\n';
    dae += '  <library_geometries>\n';
    
    allGeometries.forEach(geom => {
      const positions = geom.vertices.flat().join(' ');
      const vertCount = geom.vertices.length;
      const indicesStr = geom.indices.join(' ');
      const triangleCount = geom.indices.length / 3;
      
      dae += '    <geometry id="' + geom.name + '">\n';
      dae += '      <mesh>\n';
      dae += '        <source id="' + geom.name + '_pos">\n';
      dae += '          <float_array id="' + geom.name + '_pos_array" count="' + (vertCount * 3) + '">' + positions + '</float_array>\n';
      dae += '          <technique_common>\n';
      dae += '            <accessor source="#' + geom.name + '_pos_array" count="' + vertCount + '" stride="3">\n';
      dae += '              <param name="X" type="float"/>\n';
      dae += '              <param name="Y" type="float"/>\n';
      dae += '              <param name="Z" type="float"/>\n';
      dae += '            </accessor>\n';
      dae += '          </technique_common>\n';
      dae += '        </source>\n';
      dae += '        <vertices id="' + geom.name + '_vtx">\n';
      dae += '          <input semantic="POSITION" source="#' + geom.name + '_pos"/>\n';
      dae += '        </vertices>\n';
      dae += '        <triangles material="' + geom.matId + '" count="' + triangleCount + '">\n';
      dae += '          <input semantic="VERTEX" source="#' + geom.name + '_vtx" offset="0"/>\n';
      dae += '          <p>' + indicesStr + '</p>\n';
      dae += '        </triangles>\n';
      dae += '      </mesh>\n';
      dae += '    </geometry>\n';
    });
    
    dae += '  </library_geometries>\n';
    dae += '  <library_visual_scenes>\n';
    dae += '    <visual_scene id="Scene">\n';
    
    allGeometries.forEach(geom => {
      dae += '      <node id="' + geom.name + '_node">\n';
      dae += '        <instance_geometry url="#' + geom.name + '">\n';
      dae += '          <bind_material>\n';
      dae += '            <technique_common>\n';
      dae += '              <instance_material symbol="' + geom.matId + '" target="#' + geom.matId + '"/>\n';
      dae += '            </technique_common>\n';
      dae += '          </bind_material>\n';
      dae += '        </instance_geometry>\n';
      dae += '      </node>\n';
    });
    
    dae += '    </visual_scene>\n';
    dae += '  </library_visual_scenes>\n';
    dae += '  <scene>\n';
    dae += '    <instance_visual_scene url="#Scene"/>\n';
    dae += '  </scene>\n';
    dae += '</COLLADA>';
    
    return dae;
  };

  const resetCamera = () => {
  if (cameraRef.current) {
    const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
    const centerY = totalHeight / 2;
    
    // Scale camera distance based on tower height
    const distance = Math.max(30, totalHeight * 0.8);
    
    cameraRef.current.position.set(distance, centerY, distance);
    cameraRef.current.lookAt(0, centerY, 0);
  }
};

  const topView = () => {
    if (cameraRef.current) {
      const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
      const centerY = totalHeight / 2;
      cameraRef.current.position.set(0, totalHeight + 30, 0.1);
      cameraRef.current.lookAt(0, centerY, 0);
    }
  };

  const sideView = () => {
    if (cameraRef.current) {
      const totalHeight = sections.reduce((sum, s) => sum + s.height, 0);
      const centerY = totalHeight / 2;
      cameraRef.current.position.set(40, centerY, 0);
      cameraRef.current.lookAt(0, centerY, 0);
    }
  };
  
  //Start Parse GPS coordinates
  
  const parseCoordinates = (input) => {
  if (!input || input.trim() === '') return;
  
  const cleaned = input.trim();
  
  // Try DD format: "42.939607, -88.062806" or "42.939607,-88.062806"
  const ddPattern = /^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/;
  const ddMatch = cleaned.match(ddPattern);
  if (ddMatch) {
    setLatitude(parseFloat(ddMatch[1]));
    setLongitude(parseFloat(ddMatch[2]));
    return;
  }
  
  // Try DMS format: 42°56'22.6"N 88°03'46.1"W
  const dmsPattern = /(\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*([NSEW])\s*[,\s]*(\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*([NSEW])/i;
  const dmsMatch = cleaned.match(dmsPattern);
  if (dmsMatch) {
    const lat = parseFloat(dmsMatch[1]) + parseFloat(dmsMatch[2])/60 + parseFloat(dmsMatch[3])/3600;
    const latDir = dmsMatch[4].toUpperCase();
    const lon = parseFloat(dmsMatch[5]) + parseFloat(dmsMatch[6])/60 + parseFloat(dmsMatch[7])/3600;
    const lonDir = dmsMatch[8].toUpperCase();
    
    setLatitude(latDir === 'S' ? -lat : lat);
    setLongitude(lonDir === 'W' ? -lon : lon);
    return;
  }
  
  // Try DMM format: 42°56.377'N 88°03.768'W
  const dmmPattern = /(\d+)[°\s]+(\d+\.?\d*)['\s]*([NSEW])\s*[,\s]*(\d+)[°\s]+(\d+\.?\d*)['\s]*([NSEW])/i;
  const dmmMatch = cleaned.match(dmmPattern);
  if (dmmMatch) {
    const lat = parseFloat(dmmMatch[1]) + parseFloat(dmmMatch[2])/60;
    const latDir = dmmMatch[3].toUpperCase();
    const lon = parseFloat(dmmMatch[4]) + parseFloat(dmmMatch[5])/60;
    const lonDir = dmmMatch[6].toUpperCase();
    
    setLatitude(latDir === 'S' ? -lat : lat);
    setLongitude(lonDir === 'W' ? -lon : lon);
    return;
  }
  
  // If no format matched
  alert('Could not parse coordinates. Supported formats:\n' +
        'DD: 42.939607, -88.062806\n' +
        'DMS: 42°56\'22.6"N 88°03\'46.1"W\n' +
        'DMM: 42°56.377\'N 88°03.768\'W');
};
  
  //End Parse GPS coordinates

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <div 
		ref={containerRef} 
		className="flex-1 relative bg-slate-800 min-h-0"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
		onContextMenu={(e) => e.preventDefault()} // Added this line, prevent right clicks on canvas
      >
        <div className="absolute top-5 left-5 bg-white bg-opacity-95 p-3 rounded-lg shadow-lg">
          <button onClick={resetCamera} className="block w-full px-3 py-2 mb-2 border border-gray-300 bg-white rounded hover:bg-gray-50 text-sm font-semibold">
            Reset View
          </button>
          <button onClick={topView} className="block w-full px-3 py-2 mb-2 border border-gray-300 bg-white rounded hover:bg-gray-50 text-sm font-semibold">
            Top View
          </button>
          <button onClick={sideView} className="block w-full px-3 py-2 border border-gray-300 bg-white rounded hover:bg-gray-50 text-sm font-semibold">
            Side View
          </button>
        </div>
      </div>

      <div className="w-96 bg-white p-5 overflow-y-auto shadow-lg">
        <h1 className="text-2xl font-bold mb-2 text-gray-800">3D Tower Designer</h1>
        <p className="text-gray-600 text-sm mb-5">Design and visualize your tower structure</p>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">Tower Type</label>
          <div className="flex gap-2">
			  <button 
				onClick={() => setTowerType('monopole')}
				className={`flex-1 px-3 py-2 rounded font-semibold text-xs ${towerType === 'monopole' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
			  >
				Monopole
			  </button>
			  <button 
				onClick={() => setTowerType('lattice')}
				className={`flex-1 px-3 py-2 rounded font-semibold text-xs ${towerType === 'lattice' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
			  >
				Lattice
			  </button>
			  <button 
				onClick={() => setTowerType('guyed')}
				className={`flex-1 px-3 py-2 rounded font-semibold text-xs ${towerType === 'guyed' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
			  >
				Guyed
			  </button>
			</div>
          
        </div>
		
		<div className="mb-4">
			<label className="block text-sm font-semibold mb-2 text-gray-700">Units</label>
			<div className="flex gap-2">
				<button 
					onClick={() => setUnits('metric')}
					className={`flex-1 px-3 py-2 rounded font-semibold text-xs ${units === 'metric' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
				>
				Meters
				</button>
				<button 
					onClick={() => setUnits('imperial')}
					className={`flex-1 px-3 py-2 rounded font-semibold text-xs ${units === 'imperial' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
				>
				Feet
				</button>
			</div>
		</div>
		

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">Tower Name</label>
          <input type="text" value={towerName} onChange={(e) => setTowerName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
        </div>
		
		<div className="mb-4">
		  <label className="block text-sm font-semibold mb-2 text-gray-700">Quick Coordinates Input</label>
		  <div className="flex gap-2">
			<input 
			  type="text" 
			  value={coordinateInput} 
			  onChange={(e) => setCoordinateInput(e.target.value)}
			  onKeyPress={(e) => {
				if (e.key === 'Enter') {
				  parseCoordinates(coordinateInput);
				  setCoordinateInput('');
				}
			  }}
			  placeholder="42.939607, -88.062806"
			  className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm" 
			/>
			<button
			  onClick={() => {
				parseCoordinates(coordinateInput);
				setCoordinateInput('');
			  }}
			  className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700"
			>
			  Parse
			</button>
		  </div>
		  <p className="text-xs text-gray-500 mt-1">
			Paste coordinates from Google Maps or enter in DD, DMS, or DMM format
		  </p>
		</div>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">Latitude</label>
          <input type="number" step="0.0001" value={latitude} onChange={(e) => setLatitude(parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2 text-gray-700">Longitude</label>
          <input type="number" step="0.0001" value={longitude} onChange={(e) => setLongitude(parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
        </div>
		
		<div className="mb-4">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
			<input 
				type="checkbox" 
				checked={showSatellite} 
				onChange={(e) => setShowSatellite(e.target.checked)}
				className="w-4 h-4 cursor-pointer"
			/>
				Show Satellite Imagery
		  </label>
			<p className="text-xs text-gray-500 mt-1">Loads satellite view at tower location</p>
		</div>
		
		<div className="mb-4">
			<label className="block text-sm font-semibold mb-2 text-gray-700">Tower Azimuth (0-359°)</label>
			<input type="number" min="0" max="359" value={towerAzimuth} 
			onChange={(e) => {
			const val = parseInt(e.target.value) || 0;
			setTowerAzimuth(Math.max(0, Math.min(359, val)));
			}} 
		   className="w-full px-3 py-2 border border-gray-300 rounded text-sm" 
		 />
			<p className="text-xs text-gray-500 mt-1">Rotation for KML export in Google Earth</p>
		</div>
		
		
		<div className="mb-4 p-3 border-2 border-gray-300 rounded-lg bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-gray-700">Add Shelter</label>
            <input 
              type="checkbox" 
              checked={hasShelter} 
              onChange={(e) => setHasShelter(e.target.checked)}
              className="w-5 h-5 cursor-pointer"
            />
          </div>
          
          {hasShelter && (
            <div className="space-y-3 pt-3 border-t border-gray-300">
              <div>
			  <label className="block text-xs font-semibold mb-1 text-gray-700">Distance from Tower ({getUnitLabel()})</label>
			  <ConvertedNumberInput
				value={shelterDistance}
				onChange={setShelterDistance}
				units={units}
				toDisplay={toDisplay}
				toMeters={toMeters}
				step="0.1"
				className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
			  />
			</div>

			<div>
			  <label className="block text-xs font-semibold mb-1 text-gray-700">Position Around Tower (0-359°)</label>
			  <input 
				type="number" 
				min="0" 
				max="359" 
				value={shelterAzimuth} 
				onChange={(e) => {
				  const val = parseInt(e.target.value) || 0;
				  setShelterAzimuth(Math.max(0, Math.min(359, val)));
				}}
				className="w-full px-2 py-1 border border-gray-300 rounded text-sm" 
			  />
			  <p className="text-xs text-gray-400 mt-1">0° = North, 90° = East, 180° = South, 270° = West</p>
			</div>

			<div className="grid grid-cols-3 gap-2">
			  <div>
				  <label className="block text-xs font-semibold mb-1 text-gray-700">Length ({getUnitLabel()})</label>
				  <ConvertedNumberInput
					value={shelterLength}
					onChange={setShelterLength}
					units={units}
					toDisplay={toDisplay}
					toMeters={toMeters}
					step="0.1"
					className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
				  />
				</div>
				<div>
				  <label className="block text-xs font-semibold mb-1 text-gray-700">Width ({getUnitLabel()})</label>
				  <ConvertedNumberInput
					value={shelterWidth}
					onChange={setShelterWidth}
					units={units}
					toDisplay={toDisplay}
					toMeters={toMeters}
					step="0.1"
					className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
				  />
				</div>
				<div>
				  <label className="block text-xs font-semibold mb-1 text-gray-700">Height ({getUnitLabel()})</label>
				  <ConvertedNumberInput
					value={shelterHeight}
					onChange={setShelterHeight}
					units={units}
					toDisplay={toDisplay}
					toMeters={toMeters}
					step="0.1"
					className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
				  />
				</div>
			</div>
              
              <div>
                <label className="block text-xs font-semibold mb-1 text-gray-700">Shelter Rotation (0-359°)</label>
                <input 
                  type="number" 
                  min="0" 
                  max="359" 
                  value={shelterRotation} 
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setShelterRotation(Math.max(0, Math.min(359, val)));
                  }}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm" 
                />
                <p className="text-xs text-gray-400 mt-1">Rotates the shelter around its own center</p>
              </div>
              
              <div>
                <label className="block text-xs font-semibold mb-1 text-gray-700">Color</label>
                <input 
                  type="color" 
                  value={shelterColor} 
                  onChange={(e) => setShelterColor(e.target.value)}
                  className="w-full h-8 border border-gray-300 rounded cursor-pointer" 
                />
              </div>
            </div>
          )}
        </div>
		
		<div className="mb-4 p-3 border-2 border-gray-300 rounded-lg bg-gray-50">
		  <div className="flex items-center justify-between mb-3">
			<label className="text-sm font-semibold text-gray-700">Add Fence/Compound</label>
			<input 
			  type="checkbox" 
			  checked={hasFence} 
			  onChange={(e) => setHasFence(e.target.checked)}
			  className="w-5 h-5 cursor-pointer"
			/>
		  </div>
		  
		  {hasFence && (
			<div className="space-y-3 pt-3 border-t border-gray-300">
				<div className="grid grid-cols-2 gap-2">
					<div>
					  <label className="block text-xs font-semibold mb-1 text-gray-700">Length ({getUnitLabel()})</label>
					  <ConvertedNumberInput
						value={fenceLength}
						onChange={setFenceLength}
						units={units}
						toDisplay={toDisplay}
						toMeters={toMeters}
						step="1"
						className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
					  />
					</div>
					<div>
					  <label className="block text-xs font-semibold mb-1 text-gray-700">Width ({getUnitLabel()})</label>
					  <ConvertedNumberInput
						value={fenceWidth}
						onChange={setFenceWidth}
						units={units}
						toDisplay={toDisplay}
						toMeters={toMeters}
						step="1"
						className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
					  />
					</div>
				</div>

				<div>
				  <label className="block text-xs font-semibold mb-1 text-gray-700">Height ({getUnitLabel()})</label>
				  <ConvertedNumberInput
					value={fenceHeight}
					onChange={setFenceHeight}
					units={units}
					toDisplay={toDisplay}
					toMeters={toMeters}
					step="0.1"
					className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
				  />
				</div>

				<div>
				  <label className="block text-xs font-semibold mb-1 text-gray-700">Rotation (0-359°)</label>
				  <input 
					type="number" 
					min="0" 
					max="359" 
					value={fenceAzimuth} 
					onChange={(e) => {
					  const val = parseInt(e.target.value) || 0;
					  setFenceAzimuth(Math.max(0, Math.min(359, val)));
					}}
					className="w-full px-2 py-1 border border-gray-300 rounded text-sm" 
				  />
				</div>

				<div>
				  <label className="block text-xs font-semibold mb-1 text-gray-700">Color</label>
				  <input 
					type="color" 
					value={fenceColor} 
					onChange={(e) => setFenceColor(e.target.value)}
					className="w-full h-8 border border-gray-300 rounded cursor-pointer" 
				  />
				</div>
			</div>
		  )}
		</div>

        <button onClick={addSection} className="w-full bg-blue-600 text-white py-2 rounded mb-2 font-semibold hover:bg-blue-700">
          + Add Section
        </button>

        <button onClick={exportToKML} className="w-full bg-green-600 text-white py-2 rounded mb-4 font-semibold hover:bg-green-700">
          Export to KML + 3D Model
        </button>
		
		<button onClick={exportToPDF} className="w-full bg-purple-600 text-white py-2 rounded mb-4 font-semibold hover:bg-purple-700">
          Export PDF Report
        </button>

        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 mb-4">
          Exports complete 3D model with lattice structure, microwaves, and omni antennas for Google Earth!
        </div>

        <h3 className="text-sm font-semibold mb-3 text-gray-700">Tower Sections (Bottom to Top)</h3>
        <div className="space-y-3">
          {[...sections].reverse().map((section, idx) => (
            <div 
              key={section.id} 
              className={`border-2 rounded-lg p-3 ${selectedId === section.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-sm text-gray-800">Section {idx + 1}</span>
                <div className="flex gap-1">
                  <button onClick={() => moveSection(section.id, 'up')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-xs">↑</button>
                  <button onClick={() => moveSection(section.id, 'down')} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-xs">↓</button>
                  <button onClick={() => duplicateSection(section.id)} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 text-xs">Copy</button>
                  <button onClick={() => deleteSection(section.id)} className="px-2 py-1 bg-red-100 rounded hover:bg-red-200 text-red-600 text-xs">Del</button>
                </div>
              </div>

			  <div className="grid grid-cols-2 gap-2 mb-2">
				  <div>
					<label className="text-xs text-gray-600">{towerType === 'monopole' ? `Top Diam (${getUnitLabel()})` : `Top Width (${getUnitLabel()})`}</label>
					<ConvertedNumberInput
					  value={section.width}
					  onChange={(val) => updateSection(section.id, 'width', val)}
					  units={units}
					  toDisplay={toDisplay}
					  toMeters={toMeters}
					  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
					/>
				  </div>
				  <div>
					<label className="text-xs text-gray-600">{towerType === 'monopole' ? `Bottom Diam (${getUnitLabel()})` : `Bottom Width (${getUnitLabel()})`}</label>
					<ConvertedNumberInput
					  value={section.depth}
					  onChange={(val) => updateSection(section.id, 'depth', val)}
					  units={units}
					  toDisplay={toDisplay}
					  toMeters={toMeters}
					  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
					/>
				  </div>
				  <div>
					<label className="text-xs text-gray-600">Height ({getUnitLabel()})</label>
					<ConvertedNumberInput
					  value={section.height}
					  onChange={(val) => updateSection(section.id, 'height', val)}
					  units={units}
					  toDisplay={toDisplay}
					  toMeters={toMeters}
					  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
					/>
				  </div>
				  <div>
					<label className="text-xs text-gray-600">Color</label>
					<input type="color" value={section.color} onChange={(e) => updateSection(section.id, 'color', e.target.value)} className="w-full h-8 border border-gray-300 rounded cursor-pointer" />
				  </div>
				</div>
            </div>
          ))}
        </div>

        <h3 className="text-sm font-semibold mt-4 mb-3 text-gray-700">Microwave Antennas ({microwaves.length})</h3>
        <button onClick={addMicrowave} className="w-full bg-blue-600 text-white py-2 rounded mb-2 font-semibold hover:bg-blue-700">
          + Add Microwave
        </button>
        <div className="space-y-2 mb-4">
          {microwaves.map((microwave) => (
                  <div key={microwave.id} className="bg-gray-50 rounded p-2 mb-2 border border-gray-200">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs font-semibold text-gray-600">Microwave</span>
                      <button onClick={() => deleteMicrowave(microwave.id)} className="text-red-600 hover:text-red-800 text-xs">✕</button>
                    </div>
                    <div className="mb-2">
                      <label className="text-xs text-gray-500">Shape</label>
                      <div className="flex gap-1 mt-1">
                        <button 
                          onClick={() => updateMicrowave(microwave.id, 'shape', 'square')}
                          className={`flex-1 px-2 py-1 rounded text-xs ${microwave.shape === 'square' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Square
                        </button>
                        <button 
                          onClick={() => updateMicrowave(microwave.id, 'shape', 'circle')}
                          className={`flex-1 px-2 py-1 rounded text-xs ${microwave.shape === 'circle' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Circle
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
						<div>
						  <label className="text-xs text-gray-500">Width ({getUnitLabel()})</label>
						  <ConvertedNumberInput
							value={microwave.width}
							onChange={(val) => updateMicrowave(microwave.id, 'width', val)}
							units={units}
							toDisplay={toDisplay}
							toMeters={toMeters}
							step="0.1"
							className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
						  />
						</div>
						<div>
						  <label className="text-xs text-gray-500">Height ({getUnitLabel()})</label>
						  <ConvertedNumberInput
							value={microwave.height}
							onChange={(val) => updateMicrowave(microwave.id, 'height', val)}
							units={units}
							toDisplay={toDisplay}
							toMeters={toMeters}
							step="0.1"
							className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
						  />
						</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-xs text-gray-500">AGL ({getUnitLabel()})</label>
                        <ConvertedNumberInput
                          value={microwave.agl}
                          onChange={(val) => updateMicrowave(microwave.id, 'agl', val)}
                          units={units}
                          toDisplay={toDisplay}
                          toMeters={toMeters}
                          step="0.1"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Offset ({getUnitLabel()})</label>
                        <ConvertedNumberInput
                          value={microwave.offset}
                          onChange={(val) => updateMicrowave(microwave.id, 'offset', val)}
                          units={units}
                          toDisplay={toDisplay}
                          toMeters={toMeters}
                          step="0.1"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-xs text-gray-500">Azimuth (°)</label>
                        <input type="number" min="0" max="360" value={microwave.azimuth || 0} onChange={(e) => updateMicrowave(microwave.id, 'azimuth', parseFloat(e.target.value))} className="w-full px-2 py-1 border border-gray-300 rounded text-xs" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Color</label>
                        <input type="color" value={microwave.color || '#2563eb'} onChange={(e) => updateMicrowave(microwave.id, 'color', e.target.value)} className="w-full h-8 border border-gray-300 rounded cursor-pointer" />
                      </div>
                    </div>
                  </div>
                ))}
        </div>

        <h3 className="text-sm font-semibold mb-3 text-gray-700">Omni-Directional Antennas ({omnis.length})</h3>
        <button onClick={addOmni} className="w-full bg-red-600 text-white py-2 rounded mb-2 font-semibold hover:bg-red-700">
          + Add Omni
        </button>
        <div className="space-y-2">
          {omnis.map((omni) => (
            <div key={omni.id} className="bg-red-50 rounded p-3 border border-red-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-gray-800">Omni-Antenna</span>
                <button onClick={() => deleteOmni(omni.id)} className="text-red-600 hover:text-red-800 text-xs font-semibold">✕ Delete</button>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="text-xs text-gray-500">Diameter ({getUnitLabel()})</label>
                  <ConvertedNumberInput
                    value={omni.width}
                    onChange={(val) => updateOmni(omni.id, 'width', val)}
                    units={units}
                    toDisplay={toDisplay}
                    toMeters={toMeters}
                    step="0.1"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Height ({getUnitLabel()})</label>
                  <ConvertedNumberInput
                    value={omni.height}
                    onChange={(val) => updateOmni(omni.id, 'height', val)}
                    units={units}
                    toDisplay={toDisplay}
                    toMeters={toMeters}
                    step="0.1"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="text-xs text-gray-500">AGL ({getUnitLabel()})</label>
                  <ConvertedNumberInput
                    value={omni.agl}
                    onChange={(val) => updateOmni(omni.id, 'agl', val)}
                    units={units}
                    toDisplay={toDisplay}
                    toMeters={toMeters}
                    step="0.1"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Offset ({getUnitLabel()})</label>
                  <ConvertedNumberInput
                    value={omni.offset}
                    onChange={(val) => updateOmni(omni.id, 'offset', val)}
                    units={units}
                    toDisplay={toDisplay}
                    toMeters={toMeters}
                    step="0.1"
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Azimuth (°)</label>
                  <input type="number" min="0" max="360" value={omni.azimuth || 0} onChange={(e) => updateOmni(omni.id, 'azimuth', parseFloat(e.target.value))} className="w-full px-2 py-1 border border-gray-300 rounded text-xs" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Color</label>
                  <input type="color" value={omni.color || '#ef4444'} onChange={(e) => updateOmni(omni.id, 'color', e.target.value)} className="w-full h-8 border border-gray-300 rounded cursor-pointer" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}